import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, parseISO, addDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { supabase } from '../../lib/supabase';
import StatusBadge from '../../components/StatusBadge';
import ConfirmModal from '../../components/ConfirmModal';
import FactuurDocument from '../../components/pdf/FactuurDocument';
import { getFactuurPdfLabels, getFactuurStatusLabels, pdfFmtDate } from '../../components/pdf/pdfFactuurLabels';
import HerinneringDocument from '../../components/pdf/HerinneringDocument';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { sendPushToClient } from '../../utils/pushUtils.js';
import { clientEfVerwijderFoutTekst } from '../../utils/clientAanmaakFouten.js';
import { euro, fmtDate } from '../../utils/formatUtils.js';
import { toAmsterdamDate, toAmsterdamTime } from '../../utils/dateUtils.js';
import { unblockClient } from '../../utils/blockingUtils.js';
import { archiveInvoicePaymentLink } from '../../utils/invoiceEmailUtils';

function fmtDateTime(str) {
  if (!str) return '—';
  try { return format(parseISO(str), 'd MMM yyyy HH:mm', { locale: nl }); }
  catch { return str; }
}

function fmtLesDateTime(isoString) {
  if (!isoString) return '';
  try {
    const d = toAmsterdamDate(isoString);
    const tijd = toAmsterdamTime(isoString);
    return format(d, 'EEE d MMM', { locale: nl }) + ', ' + tijd;
  } catch {
    return '';
  }
}

function fmtCreditReden(ct) {
  const label = t('creditReasons.' + ct.reason) !== 'creditReasons.' + ct.reason
    ? t('creditReasons.' + ct.reason)
    : ct.reason;
  if (ct.group_lessons?.name) {
    const dateStr = ct.group_lessons.starts_at ? ' — ' + fmtLesDateTime(ct.group_lessons.starts_at) : '';
    return label + ' — ' + ct.group_lessons.name + dateStr;
  }
  return label;
}

function calcEndDate(membership) {
  if (!membership) return null;
  if (membership.end_date) return membership.end_date;
  if (membership.initial_end_date) return membership.initial_end_date;
  if (membership.start_date && membership.initial_duration_months) {
    const d = new Date(membership.start_date + 'T00:00:00');
    d.setDate(d.getDate() + membership.initial_duration_months * 31);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

const VOLGENDE_STATUS = {
  draft:           'sent',
  sent:            'paid',
  reminded:        'paid',
  final_notice:    'paid',
  collections:     'paid',
  partially_paid:  'paid',
};
const VOLGENDE_LABEL = {
  draft:          () => t('facturen.markeerVerzonden'),
  sent:           () => t('facturen.markeerBetaald'),
  reminded:       () => t('facturen.markeerBetaald'),
  final_notice:   () => t('facturen.markeerBetaald'),
  collections:    () => t('facturen.markeerBetaaldIncasso'),
  partially_paid: () => t('facturen.volledigBetaaldKnop'),
};

function emptyLine() {
  return { description: '', quantity: '1', unit_price: '', btw_percentage: 9, price_mode: 'excl' };
}

function lineUnitPriceExcl(l) {
  const raw = parseFloat(l.unit_price) || 0;
  if (l.price_mode === 'incl') {
    return raw / (1 + (l.btw_percentage || 0) / 100);
  }
  return raw;
}

function lineExcl(l) {
  return (parseFloat(l.quantity) || 0) * lineUnitPriceExcl(l);
}
function lineBtw(l) {
  return Math.round(lineExcl(l) * (l.btw_percentage / 100) * 100) / 100;
}
function lineIncl(l) { return lineExcl(l) + lineBtw(l); }

function calcTotals(lines) {
  const subtotal = lines.reduce((s, l) => s + lineExcl(l), 0);
  const btwMap = {};
  lines.forEach(l => { btwMap[l.btw_percentage] = (btwMap[l.btw_percentage] || 0) + lineBtw(l); });
  const totalBtw = Object.values(btwMap).reduce((s, b) => s + b, 0);
  return { subtotal, btwMap, totalBtw, total: subtotal + totalBtw };
}

function NieuweFactuurModal({ clientId, clientName, memberships = [], onClose, onCreated }) {
  const [periodStart, setPeriodStart] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd')
  );
  const [periodEnd, setPeriodEnd] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'yyyy-MM-dd')
  );
  const [lines, setLines] = useState([emptyLine()]);
  const activeMemberships = memberships.filter(m => m.status === 'active');
  const [membershipId, setMembershipId] = useState(
    activeMemberships.length === 1 ? activeMemberships[0].id : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function updateLine(idx, field, val) {
    setLines(prev => { const next = [...prev]; next[idx] = { ...next[idx], [field]: val }; return next; });
  }

  const { subtotal, btwMap, totalBtw, total } = calcTotals(lines);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (lines.some(l => !l.description || !l.unit_price)) { setError(t('facturen.validatieRegels')); return; }
    if (new Date(periodEnd) <= new Date(periodStart)) { setError(t('facturen.validatieDatum')); return; }
    setSaving(true);
    try {
      const { data: invoiceId, error: rpcErr } = await supabase.rpc('create_invoice', {
        p_client_id: clientId,
        p_lines: lines.map(l => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: lineUnitPriceExcl(l),
          btw_percentage: l.btw_percentage,
        })),
        p_period_start: periodStart,
        p_period_end: periodEnd,
        p_client_membership_id: membershipId || null,
      });
      if (rpcErr) throw rpcErr;
      onCreated(invoiceId);
    } catch (err) {
      setError(t('facturen.foutAanmaken') + err.message);
    } finally {
      setSaving(false);
    }
  }

  const gridCols = '3fr 70px 160px 70px 90px 90px 36px';

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('facturen.modalTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelClient')}</label>
              <div className="form-input" style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}>{clientName}</div>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelAbonnement')}</label>
              <select className="form-input" value={membershipId} onChange={e => setMembershipId(e.target.value)}>
                <option value="">{t('facturen.geenAbonnementKoppeling')}</option>
                {activeMemberships.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.is_custom ? m.custom_name : (m.membership_types?.name || 'Abonnement')}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{t('facturen.abonnementKoppelingHint')}</p>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelPeriodeStart')}</label>
              <input type="date" className="form-input" value={periodStart} onChange={e => setPeriodStart(e.target.value)} required />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelPeriodeEinde')}</label>
              <input type="date" className="form-input" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} required />
            </div>
          </div>

          <div className="divider" />
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t('facturen.regeloverzicht')}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, marginBottom: 8 }}>
            {[t('facturen.colOmschrijving'), t('facturen.colAantal'), t('facturen.colStukprijs'), t('facturen.colBtwPct'), t('facturen.colExcl'), t('facturen.colIncl'), ''].map((h, i) => (
              <span key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</span>
            ))}
          </div>

          {lines.map((line, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <input className="form-input" placeholder={t('facturen.omschrijvingPlaceholder')} value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)} required />
              <input className="form-input" type="number" min="0.01" step="0.01" placeholder="1" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} required />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={line.unit_price}
                    onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                    required
                    style={{ flex: 1 }}
                  />
                  <div style={{
                    display: 'flex', flexDirection: 'row',
                    borderRadius: 20, border: '1px solid var(--color-primary)',
                    overflow: 'hidden', marginLeft: 8,
                  }}>
                    <button type="button" onClick={() => updateLine(idx, 'price_mode', 'excl')} style={{ fontSize: 10, paddingLeft: 8, paddingRight: 8, paddingTop: 4, paddingBottom: 4, cursor: 'pointer', border: 'none', background: line.price_mode !== 'incl' ? 'var(--color-primary)' : '#fff', color: line.price_mode !== 'incl' ? '#fff' : 'var(--color-primary)' }}>
                      {t('facturen.stukprijsExcl')}
                    </button>
                    <button type="button" onClick={() => updateLine(idx, 'price_mode', 'incl')} style={{ fontSize: 10, paddingLeft: 8, paddingRight: 8, paddingTop: 4, paddingBottom: 4, cursor: 'pointer', border: 'none', background: line.price_mode === 'incl' ? 'var(--color-primary)' : '#fff', color: line.price_mode === 'incl' ? '#fff' : 'var(--color-primary)' }}>
                      {t('facturen.stukprijsIncl')}
                    </button>
                  </div>
                </div>
                {line.price_mode === 'incl' && (parseFloat(line.unit_price) || 0) > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                    {t('facturen.exclRef')} {euro(lineUnitPriceExcl(line))}
                  </div>
                )}
              </div>
              <select className="form-input" value={line.btw_percentage} onChange={e => updateLine(idx, 'btw_percentage', Number(e.target.value))}>
                <option value={9}>{t('facturen.btwOptie9')}</option>
                <option value={21}>{t('facturen.btwOptie21')}</option>
              </select>
              <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{euro(lineExcl(line))}</span>
              <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{euro(lineIncl(line))}</span>
              {lines.length > 1 ? (
                <button type="button" onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', fontSize: 16, cursor: 'pointer' }}>✕</button>
              ) : <span />}
            </div>
          ))}

          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLines(prev => [...prev, emptyLine()])} style={{ marginBottom: 20 }}>
            {t('facturen.regelToevoegen')}
          </button>

          <div className="divider" />
          <div style={{ maxWidth: 300, marginLeft: 'auto', display: 'grid', gap: 8 }}>
            <TotalRow label={t('facturen.subtotaal')} value={euro(subtotal)} />
            {Object.entries(btwMap).sort(([a], [b]) => a - b).map(([pct, amt]) => (
              <TotalRow key={pct} label={`${t('facturen.btw')} ${pct}%`} value={euro(amt)} />
            ))}
            <div style={{ height: 1, background: 'var(--color-border)' }} />
            <TotalRow label={t('facturen.totaalIncl')} value={euro(total)} bold />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('facturen.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('facturen.opslaan') : t('facturen.aanmaken')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--color-text-muted)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 400, fontSize: bold ? 16 : 14 }}>{value}</span>
    </div>
  );
}

function getPoolColumn(sessionType) {
  if (sessionType === 'pt') return 'current_pt_credits';
  if (sessionType === 'pt_plus') return 'current_pt_plus_credits';
  if (sessionType === 'small_group') return 'current_small_group_credits';
  return 'current_credits';
}

function getPoolCredits(memb) {
  if (!memb) return 0;
  if (memb.session_type === 'pt') return memb.current_pt_credits ?? 0;
  if (memb.session_type === 'pt_plus') return memb.current_pt_plus_credits ?? 0;
  if (memb.session_type === 'small_group') return memb.current_small_group_credits ?? 0;
  return memb.current_credits ?? 0;
}

function CreditModal({ type, memberships, onClose, onDone }) {
  const autoId = memberships.length === 1 ? memberships[0].id : '';
  const [selectedId, setSelectedId] = useState(autoId);
  const [aantal, setAantal] = useState('');
  const [reden, setReden] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isBij = type === 'bij';
  const selectedMemb = memberships.find(m => m.id === selectedId) || null;
  const huidigeSaldo = getPoolCredits(selectedMemb);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedId) { setError(t('clientDetail.creditFoutKiesAbonnement')); return; }
    const n = parseInt(aantal);
    if (!n || n < 1) { setError(t('clientDetail.creditFoutAantal')); return; }
    if (!isBij && n > huidigeSaldo) { setError(t('clientDetail.creditFoutOnvoldoende')); return; }

    setSaving(true);
    setError('');
    try {
      const delta = isBij ? n : -n;

      // R2: atomaire server-side mutatie — delta-update + transactieregel
      // in één transactie; de RPC retourneert de werkelijke nieuwe balans.
      const { data, error: rpcErr } = await supabase.rpc('mutate_membership_credits', {
        p_client_membership_id: selectedId,
        p_delta: delta,
        p_note: reden || null,
      });
      if (rpcErr) {
        if (rpcErr.message?.includes('saldo_onvoldoende')) {
          setError(t('clientDetail.creditFoutOnvoldoende'));
          return;
        }
        throw rpcErr;
      }

      onDone({
        membershipId: selectedId,
        poolColumn: data.pool_column,
        nieuweBalans: data.new_balance,
      });
    } catch (err) {
      setError(t('clientDetail.creditFoutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isBij ? t('clientDetail.creditModalTitelBij') : t('clientDetail.creditModalTitelAf')}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          {memberships.length > 1 && (
            <div className="form-group">
              <label className="form-label">{t('clientDetail.creditModalKiesAbonnement')}</label>
              <select
                className="form-input"
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                required
              >
                <option value="">{t('clientDetail.creditModalKiesAbonnementPlaceholder')}</option>
                {memberships.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.membership_types?.name ?? m.session_type} ({getPoolCredits(m)} credits)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('clientDetail.creditModalAantal')}</label>
            <input className="form-input" type="number" min="1" value={aantal} onChange={e => setAantal(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">{t('clientDetail.creditModalReden')}</label>
            <input className="form-input" value={reden} onChange={e => setReden(e.target.value)} placeholder="Optioneel" />
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('clientDetail.creditModalAnnuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('clientDetail.creditModalOpslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KcalModal({ clientId, coachId, initialValues, onClose, onSaved }) {
  const [form, setForm] = useState({
    daily_calories: initialValues?.daily_calories ?? '',
    protein_target: initialValues?.protein_target ?? '',
    carbs_target:   initialValues?.carbs_target   ?? '',
    fat_target:     initialValues?.fat_target     ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.daily_calories || isNaN(Number(form.daily_calories))) {
      setError(t('voeding.valDagKcal'));
      return;
    }
    setSaving(true);
    try {
      const { error: err } = await supabase.from('calorie_assignments').insert({
        client_id:      clientId,
        coach_id:       coachId,
        daily_calories: Number(form.daily_calories),
        protein_target: form.protein_target !== '' ? Number(form.protein_target) : null,
        carbs_target:   form.carbs_target   !== '' ? Number(form.carbs_target)   : null,
        fat_target:     form.fat_target     !== '' ? Number(form.fat_target)     : null,
      });
      if (err) throw err;
      onSaved();
    } catch (err) {
      setError(t('voeding.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('voeding.modalTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">{t('voeding.labelDagKcal')}</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.daily_calories}
              onChange={e => setForm(p => ({ ...p, daily_calories: e.target.value }))}
              required
              autoFocus
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, alignItems: 'flex-end' }}>
            {[
              { key: 'protein_target', label: t('voeding.labelEiwitten') },
              { key: 'carbs_target',   label: t('voeding.labelKoolhydraten') },
              { key: 'fat_target',     label: t('voeding.labelVetten') },
            ].map(({ key, label }) => (
              <div className="form-group" key={key} style={{ marginBottom: 0 }}>
                <label className="form-label">{label}</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder={t('voeding.optioneel')}
                  value={form[key]}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('voeding.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('voeding.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { showSuccess, showError } = useToast();

  const [client, setClient] = useState(null);
  const [membership, setMembership] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [credits, setCredits] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState({});
  const [company, setCompany] = useState({});
  const [afspraken, setAfspraken] = useState([]);
  const [membershipHistory, setMembershipHistory] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [pendingTokens, setPendingTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showNieuweFactuur, setShowNieuweFactuur] = useState(false);
  const [creditModal, setCreditModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [creditnotaConfirm, setCreditnotaConfirm] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [incassoConfirm, setIncassoConfirm] = useState(null);
  const [expandedTimelines, setExpandedTimelines] = useState(new Set());
  const [betaaldConfirm, setBetaaldConfirm] = useState(null);
  const [onbetaaldConfirm, setOnbetaaldConfirm] = useState(null);
  const [gedeeltelijkModal, setGedeeltelijkModal] = useState(null);
  const [volledigBetaaldConfirm, setVolledigBetaaldConfirm] = useState(null);
  const [testmodus, setTestmodus] = useState(false);
  const [sendingProspectReminder, setSendingProspectReminder] = useState(false);

  // Persoonsgegevens bewerken
  const [persoonEdit, setPersoonEdit] = useState(false);
  const [persoonForm, setPersoonForm] = useState({});
  const [persoonSaving, setPersoonSaving] = useState(false);
  const [persoonError, setPersoonError] = useState('');
  const [locations, setLocations] = useState([]);

  // Lidmaatschap — drie acties model
  const [beeindigenModal, setBeeindigenModal] = useState(null); // null | { membershipId }
  const [beeindigenOptie, setBeeindigenOptie] = useState('einde_periode'); // 'einde_periode' | 'einde_contract' | 'aangepast'
  const [beeindigenDatum, setBeeindigenDatum] = useState('');
  const [beeindigenSaving, setBeeindigenSaving] = useState(false);
  const [beeindigenError, setBeeindigenError] = useState('');
  const [nieuwModal, setNieuwModal] = useState(false);
  const [nieuwForm, setNieuwForm] = useState({ type_id: '', staffel_id: '', start_date: new Date().toISOString().slice(0, 10), prijs: '', credits: '', payment_frequency: '', welkomstmail: false });
  const [nieuwStaffels, setNieuwStaffels] = useState([]);
  const [nieuwSaving, setNieuwSaving] = useState(false);
  const [nieuwError, setNieuwError] = useState('');
  const [alleHistorie, setAlleHistorie] = useState([]);
  const [historieLoading, setHistorieLoading] = useState(false);

  // Legacy — bewaard voor 'opslaan als nieuw type' in overeenkomst-modal
  const [membEdit, setMembEdit] = useState(false);
  const [membForm, setMembForm] = useState({});
  const [membSaving, setMembSaving] = useState(false);
  const [membError, setMembError] = useState('');
  const [customEnabled, setCustomEnabled] = useState(false);
  const [customForm, setCustomForm] = useState({
    custom_name: '',
    custom_price: '',
    custom_credits: '',
    custom_duration_days: '',
    save_as_new_type: false,
  });

  // Afspraken weergave
  const [showAllAankomend, setShowAllAankomend] = useState(false);
  const [showAllGeschiedenis, setShowAllGeschiedenis] = useState(false);

  // Voeding & Kcal
  const [kcalAssignment, setKcalAssignment] = useState(null);
  const [kcalModal, setKcalModal] = useState(false);
  const [kcalLoading, setKcalLoading] = useState(false);

  // E-mail automatisering
  const [emailAutoMode, setEmailAutoMode] = useState('system'); // 'system' | 'custom'
  const [emailAutoSettings, setEmailAutoSettings] = useState({ auto_reminder: true, auto_final_notice: false });
  const [emailAutoSystemDefaults, setEmailAutoSystemDefaults] = useState({ email_auto_reminder: 'true', email_auto_final_notice: 'false' });
  const [emailAutoSaving, setEmailAutoSaving] = useState(false);

  // F4-10: toegestane betaalmethoden per client (subset van systeembreed)
  const [pmSystem, setPmSystem] = useState({ ideal: false, sepa: false });
  const [pmMode, setPmMode] = useState('system'); // 'system' (geen restrictie) | 'custom'
  const [pmClient, setPmClient] = useState({ ideal: true, sepa: true });
  const [pmSaving, setPmSaving] = useState(false);

  // F4-10 stap4a: payment_method wijzigen op bestaande membership
  const [pmChangeTarget, setPmChangeTarget] = useState(null); // membershipId string
  const [pmChangeValue, setPmChangeValue] = useState('manual');
  const [pmChangeSaving, setPmChangeSaving] = useState(false);

  // Overeenkomst herinnering override (UITBREIDING 9)
  const [agrReminderMode, setAgrReminderMode] = useState('system');
  const [agrReminderSettings, setAgrReminderSettings] = useState({ enabled: true, interval_days: 3, max_reminders: 2 });
  const [agrReminderSaving, setAgrReminderSaving] = useState(false);

  // Abonnement herinneringen (F4-3)
  const [membReminderMode, setMembReminderMode] = useState('system'); // 'system' | 'custom' | 'disabled'
  const [membReminderDays, setMembReminderDays] = useState({ 14: true, 7: true, 3: true });
  const [membReminderSaving, setMembReminderSaving] = useState(false);
  const [membReminderSystemDefault, setMembReminderSystemDefault] = useState('14,7,3');

  // Communicatielogboek
  const [commLog, setCommLog] = useState([]);
  const [commLoading, setCommLoading] = useState(false);
  const [commOffset, setCommOffset] = useState(0);
  const [commHasMore, setCommHasMore] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Overeenkomst intrekken
  const [revokeConfirm, setRevokeConfirm] = useState(null); // tokenId string
  const [revoking, setRevoking] = useState(false);
  const [allTokens, setAllTokens] = useState([]); // BUG 18: alle tokens incl verlopen/afgewezen
  const [copiedTokenId, setCopiedTokenId] = useState(null); // BUG 16
  const [resendingTokenId, setResendingTokenId] = useState(null); // BUG 16
  const [deleteClientConfirm, setDeleteClientConfirm] = useState(false); // BUG 13
  const [deletingClient, setDeletingClient] = useState(false); // BUG 13
  const [unblockConfirm, setUnblockConfirm] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [welkomstmailConfirm, setWelkomstmailConfirm] = useState(false);
  const [stuuringWelkomstmail, setStuuringWelkomstmail] = useState(false);
  const [opzegModal, setOpzegModal] = useState(null); // { membershipId, cancDate }

  useEffect(() => {
    load();
    loadCommLog(true);
    loadKcal();
    loadEmailAutoSettings();
    loadPaymentMethods();
    loadLocations();
  }, [id, user?.id, role]);

  async function loadPaymentMethods() {
    try {
      const { data: sysData } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['payment_method_ideal_enabled', 'payment_method_sepa_enabled']);
      const sysMap = {};
      (sysData || []).forEach(({ key, value }) => { sysMap[key] = value; });
      const sysIdeal = sysMap.payment_method_ideal_enabled === 'true';
      const sysSepa = sysMap.payment_method_sepa_enabled === 'true';
      setPmSystem({ ideal: sysIdeal, sepa: sysSepa });

      const { data: prof } = await supabase
        .from('profiles')
        .select('allowed_payment_methods')
        .eq('id', id)
        .maybeSingle();
      const allowed = prof?.allowed_payment_methods ?? null;
      if (allowed === null) {
        // Geen restrictie: alle systeembreed ingeschakelde methoden.
        setPmMode('system');
        setPmClient({ ideal: true, sepa: true });
      } else {
        setPmMode('custom');
        setPmClient({ ideal: allowed.includes('ideal'), sepa: allowed.includes('sepa') });
      }
    } catch (err) {
      console.error('loadPaymentMethods error:', err);
    }
  }

  async function handleSavePaymentMethods() {
    setPmSaving(true);
    try {
      let p_methods = null;
      if (pmMode === 'custom') {
        // Alleen systeembreed ingeschakelde én aangevinkte methoden meesturen.
        p_methods = [];
        if (pmSystem.ideal && pmClient.ideal) p_methods.push('ideal');
        if (pmSystem.sepa && pmClient.sepa) p_methods.push('sepa');
      }
      const { error: err } = await supabase.rpc('set_client_allowed_payment_methods', {
        p_client_id: id,
        p_methods,
      });
      if (err) throw err;
      showSuccess(t('clients.betaalmethodenOpgeslagen'));
      await loadPaymentMethods();
    } catch (err) {
      showError(t('clients.betaalmethodenFout') + err.message);
    } finally {
      setPmSaving(false);
    }
  }

  // F4-10 stap4a: door coach/admin toegestane methoden voor déze client
  // (systeem ∩ client-subset, altijd incl. manual) — zelfde berekening als
  // client_allowed_payment_methods(uuid), afgeleid uit al geladen pmSystem/pmClient.
  function computeAllowedPaymentMethods() {
    const allowed = ['manual'];
    if (pmSystem.ideal && pmClient.ideal) allowed.push('ideal');
    if (pmSystem.sepa && pmClient.sepa) allowed.push('sepa');
    return allowed;
  }

  async function handleConfirmChangePaymentMethod(membershipId) {
    if (pmChangeValue === 'sepa') return; // geblokkeerd in de UI, geen RPC-aanroep nodig
    setPmChangeSaving(true);
    try {
      const { error: err } = await supabase.rpc('change_client_payment_method', {
        p_membership_id: membershipId,
        p_new_payment_method: pmChangeValue,
      });
      if (err) throw err;
      showSuccess(t('clientDetail.betaalmethodeOpgeslagen'));
      setPmChangeTarget(null);
      setMemberships(prev => prev.map(m => m.id === membershipId ? { ...m, payment_method: pmChangeValue } : m));
    } catch (err) {
      showError(t('clientDetail.betaalmethodeFout') + err.message);
    } finally {
      setPmChangeSaving(false);
    }
  }

  async function loadLocations() {
    try {
      const { data } = await supabase
        .from('locations')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      setLocations(data || []);
    } catch (err) {
      console.error('Fout bij laden locaties:', err);
    }
  }

  async function loadKcal() {
    setKcalLoading(true);
    try {
      const { data } = await supabase
        .from('calorie_assignments')
        .select('id, daily_calories, protein_target, carbs_target, fat_target, created_at')
        .eq('client_id', id)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      setKcalAssignment(data || null);
    } catch { /* stil falen */ } finally {
      setKcalLoading(false);
    }
  }

  async function loadEmailAutoSettings() {
    try {
      // Haal systeeminstellingen op
      const { data: sysData } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['email_auto_reminder', 'email_auto_final_notice']);

      const sysMap = {};
      (sysData || []).forEach(({ key, value }) => { sysMap[key] = value; });
      setEmailAutoSystemDefaults(sysMap);

      // Haal client override op
      const { data: clientData } = await supabase
        .from('client_email_automation')
        .select('auto_reminder, auto_final_notice')
        .eq('client_id', id)
        .maybeSingle();

      if (clientData && (clientData.auto_reminder !== null || clientData.auto_final_notice !== null)) {
        setEmailAutoMode('custom');
        setEmailAutoSettings({
          auto_reminder: clientData.auto_reminder ?? (sysMap.email_auto_reminder === 'true'),
          auto_final_notice: clientData.auto_final_notice ?? (sysMap.email_auto_final_notice === 'true'),
        });
      } else {
        setEmailAutoMode('system');
        setEmailAutoSettings({
          auto_reminder: sysMap.email_auto_reminder === 'true',
          auto_final_notice: sysMap.email_auto_final_notice === 'true',
        });
      }

      // UITBREIDING 9: Haal overeenkomst herinnering override op
      const { data: agrOverride } = await supabase
        .from('agreement_reminder_settings')
        .select('enabled, interval_days, max_reminders')
        .eq('client_id', id)
        .maybeSingle();

      if (agrOverride) {
        setAgrReminderMode('custom');
        setAgrReminderSettings({
          enabled: agrOverride.enabled ?? true,
          interval_days: agrOverride.interval_days ?? 3,
          max_reminders: agrOverride.max_reminders ?? 2,
        });
      } else {
        setAgrReminderMode('system');
      }
    } catch (err) {
      console.error('loadEmailAutoSettings error:', err);
    }
  }

  async function handleSaveAgrReminder() {
    setAgrReminderSaving(true);
    try {
      if (agrReminderMode === 'system') {
        await supabase.from('agreement_reminder_settings').delete().eq('client_id', id);
      } else {
        await supabase.from('agreement_reminder_settings').upsert({
          client_id: id,
          enabled: agrReminderSettings.enabled,
          interval_days: agrReminderSettings.interval_days,
          max_reminders: agrReminderSettings.max_reminders,
        }, { onConflict: 'client_id' });
      }
      showSuccess(t('clients.agrReminderOpgeslagen'));
    } catch (err) {
      showError(t('clients.agrReminderFout') + err.message);
    } finally {
      setAgrReminderSaving(false);
    }
  }

  async function handleSaveMembReminder() {
    if (!membership) return;
    setMembReminderSaving(true);
    try {
      let overrideValue = null;
      if (membReminderMode === 'disabled') {
        overrideValue = '';
      } else if (membReminderMode === 'custom') {
        const selected = Object.entries(membReminderDays).filter(([, v]) => v).map(([k]) => k);
        overrideValue = selected.sort((a, b) => Number(b) - Number(a)).join(',');
      }
      const { error: updErr } = await supabase
        .from('client_memberships')
        .update({ reminder_days_override: overrideValue })
        .eq('id', membership.id);
      if (updErr) throw updErr;
      showSuccess(t('clientDetail.membReminderOpgeslagen'));
    } catch (err) {
      showError(t('clientDetail.membReminderFout') + err.message);
    } finally {
      setMembReminderSaving(false);
    }
  }

  async function handleSaveEmailAuto() {
    setEmailAutoSaving(true);
    try {
      if (emailAutoMode === 'system') {
        // Verwijder client override (terug naar systeem)
        const { error: delErr } = await supabase
          .from('client_email_automation')
          .delete()
          .eq('client_id', id);
        if (delErr) throw delErr;
      } else {
        // Sla custom instellingen op
        const { error: upsertErr } = await supabase
          .from('client_email_automation')
          .upsert({
            client_id: id,
            auto_reminder: emailAutoSettings.auto_reminder,
            auto_final_notice: emailAutoSettings.auto_final_notice,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'client_id' });
        if (upsertErr) throw upsertErr;
      }
      showSuccess(t('clients.emailAutomatiseringOpgeslagen'));
    } catch (err) {
      showError(t('clients.emailAutomatiseringFout') + err.message);
    } finally {
      setEmailAutoSaving(false);
    }
  }


  async function handleRevokeToken(tokenId) {
    setRevoking(true);
    try {
      const { error } = await supabase
        .from('agreement_tokens')
        .update({
          rejected_at: new Date().toISOString(),
          rejected_by: user.id,
        })
        .eq('id', tokenId);
      if (error) throw error;
      setRevokeConfirm(null);
      showSuccess(t('agreement.ingetrokkenSuccess'));
      await load();
      await loadCommLog(true);
    } catch (err) {
      showError(t('agreement.intrekkenFout') + err.message);
    } finally {
      setRevoking(false);
    }
  }

  async function downloadAgreementPDF(pdfPath) {
    const { data, error } = await supabase.storage
      .from('agreements')
      .createSignedUrl(pdfPath, 300);
    if (error || !data?.signedUrl) { showError(t('agreement.downloadFout')); return; }
    window.open(data.signedUrl, '_blank');
  }

  // BUG 13: Client verwijderen via Edge Function
  async function handleDeleteClient() {
    setDeletingClient(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke('delete-client', {
        body: { client_id: id },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (res.error) {
        // Bij een non-2xx zit de body niet in res.data maar in error.context
        // (FunctionsHttpError). Zonder dit uitlezen zou de coach alleen een
        // generieke "non-2xx status"-melding zien i.p.v. de echte foutcode —
        // en juist auth_verwijderen_mislukt móet zichtbaar zijn.
        let body = null;
        try { body = await res.error.context?.json(); } catch { /* geen JSON-body */ }
        throw new Error(body?.error ? clientEfVerwijderFoutTekst(body) : (res.error.message || t('clients.efFoutVerwijderOnbekend')));
      }
      if (res.data?.error) throw new Error(clientEfVerwijderFoutTekst(res.data));
      showSuccess(t('clients.verwijderSuccess'));
      navigate('/coach/clients');
    } catch (err) {
      showError(t('clients.verwijderFout') + err.message);
    } finally {
      setDeletingClient(false);
      setDeleteClientConfirm(false);
    }
  }

  async function handleOpzeggenConfirm() {
    if (!opzegModal) return;
    const { membershipId, cancDate } = opzegModal;
    setOpzegModal(null);
    try {
      const { error: rpcErr } = await supabase.rpc('cancel_client_membership', {
        p_membership_id: membershipId,
        p_cancellation_date: cancDate,
      });
      if (rpcErr) throw rpcErr;

      try {
        await supabase.functions.invoke('send-cancellation-confirmation', {
          body: { client_id: id, cancellation_date: cancDate },
        });
      } catch (_) { /* notificaties niet-fataal */ }

      showSuccess(t('clientDetail.opzeggenSucces'));
      await load();
    } catch (err) {
      showError(t('clientDetail.opzeggenFout') + err.message);
    }
  }

  // ─── Actie 1: Beëindigen ──────────────────────────────────────
  async function handleBeeindigenConfirm() {
    setBeeindigenSaving(true);
    setBeeindigenError('');
    try {
      let cancDate;
      const today = new Date().toISOString().slice(0, 10);
      if (beeindigenOptie === 'aangepast') {
        if (!beeindigenDatum || beeindigenDatum <= today) {
          setBeeindigenError(t('clientDetail.beeindigenDatumLabel') + ' (minimaal morgen)');
          setBeeindigenSaving(false);
          return;
        }
        cancDate = beeindigenDatum;
      } else if (beeindigenOptie === 'einde_contract' && membership?.initial_end_date) {
        cancDate = membership.initial_end_date;
      } else {
        const anchor = membership?.last_credit_booking_at
          ? new Date(membership.last_credit_booking_at)
          : new Date(membership?.start_date || today);
        const end = new Date(anchor.getTime() + 30 * 86400000);
        cancDate = end.toISOString().split('T')[0];
      }

      const { error: rpcErr } = await supabase.rpc('cancel_client_membership', {
        p_membership_id: beeindigenModal.membershipId,
        p_cancellation_date: cancDate,
      });
      if (rpcErr) throw rpcErr;

      try {
        await supabase.functions.invoke('send-cancellation-confirmation', {
          body: { client_id: id, cancellation_date: cancDate },
        });
      } catch (_) { /* notificaties niet-fataal */ }

      showSuccess(t('clientDetail.beeindigenSucces'));
      setBeeindigenModal(null);
      await load();
    } catch (err) {
      setBeeindigenError(t('clientDetail.beeindigenFout') + err.message);
    } finally {
      setBeeindigenSaving(false);
    }
  }

  // ─── Actie 2: Nieuw lidmaatschap ─────────────────────────────
  function filterBeschikbareStaffels(staffels) {
    const today = new Date().toISOString().slice(0, 10);
    return (staffels || []).filter(p =>
      p.is_active
      && (p.available_from == null || p.available_from <= today)
      && (p.available_until == null || p.available_until >= today)
    ).sort((a, b) => a.duration_months - b.duration_months);
  }

  async function loadStaffelsVoorType(typeId) {
    if (!typeId) { setNieuwStaffels([]); return; }
    const type = membershipTypes.find(mt => mt.id === typeId);
    const staffels = filterBeschikbareStaffels(type?.membership_type_periods);
    setNieuwStaffels(staffels);
    return staffels;
  }

  async function handleNieuwTypeChange(typeId) {
    const type = membershipTypes.find(mt => mt.id === typeId);
    const staffels = filterBeschikbareStaffels(type?.membership_type_periods);
    const defaultStaffel = staffels[0] || null;
    setNieuwStaffels(staffels);
    const defaultFreq = defaultStaffel?.payment_frequency !== 'both' ? (defaultStaffel?.payment_frequency || '') : '';
    setNieuwForm(prev => ({
      ...prev,
      type_id: typeId,
      staffel_id: defaultStaffel?.id || '',
      prijs: defaultStaffel?.price_per_period?.toString() || '',
      credits: type?.legacy_credits_per_period?.toString() || '',
      payment_frequency: defaultFreq,
      period_discount_pct: Number(defaultStaffel?.period_discount_pct || 0),
    }));
  }

  async function handleNieuwStaffelChange(staffelId) {
    const staffel = nieuwStaffels.find(s => s.id === staffelId);
    const freq = staffel?.payment_frequency !== 'both' ? (staffel?.payment_frequency || '') : '';
    setNieuwForm(prev => ({
      ...prev,
      staffel_id: staffelId,
      prijs: staffel?.price_per_period?.toString() || prev.prijs,
      payment_frequency: freq,
      period_discount_pct: Number(staffel?.period_discount_pct || 0),
    }));
  }

  async function handleNieuwOpslaan() {
    setNieuwError('');
    if (!nieuwForm.type_id) { setNieuwError(t('clientDetail.nieuwValidatieType')); return; }
    if (!nieuwForm.staffel_id) { setNieuwError(t('clientDetail.nieuwValidatieStaffel')); return; }
    if (!nieuwForm.start_date) { setNieuwError(t('clientDetail.nieuwValidatieStartdatum')); return; }
    const staffelForCheck = nieuwStaffels.find(s => s.id === nieuwForm.staffel_id);
    if (staffelForCheck?.payment_frequency === 'both' && !nieuwForm.payment_frequency) {
      setNieuwError(t('overeenkomstBetaalfreq.verplicht')); return;
    }

    setNieuwSaving(true);
    try {
      const type = membershipTypes.find(mt => mt.id === nieuwForm.type_id);
      const staffel = nieuwStaffels.find(s => s.id === nieuwForm.staffel_id);
      const prijs = parseFloat(nieuwForm.prijs) || 0;
      const credits = parseInt(nieuwForm.credits) || 0;
      const typeCredits = type?.legacy_credits_per_period || 0;
      const staffelPrijs = staffel?.price_per_period || 0;
      const isCustom = prijs !== staffelPrijs || credits !== typeCredits;
      const durationMonths = staffel?.duration_months || 1;
      const isRolling = durationMonths === 1;
      const startDateObj = new Date(nieuwForm.start_date + 'T00:00:00');
      const endDateObj = new Date(startDateObj);
      endDateObj.setMonth(endDateObj.getMonth() + durationMonths);
      const initialEndDate = !isRolling ? endDateObj.toISOString().split('T')[0] : null;
      const effectiveFreq = staffel?.payment_frequency === 'both'
        ? nieuwForm.payment_frequency
        : (staffel?.payment_frequency || 'period');

      const discountPct = ['period', 'both'].includes(staffel?.payment_frequency || '')
        ? (nieuwForm.period_discount_pct || 0)
        : 0;

      const sTypeNieuw = type?.session_type || 'pt';
      const sessionsNieuw = type?.sessions_per_week_frequency || 0;
      const creditsPerMonthNieuw = Math.ceil(sessionsNieuw * 4.33) || credits;

      const insertData = {
        client_id: id,
        membership_type_id: nieuwForm.type_id,
        start_date: nieuwForm.start_date,
        end_date: null,
        current_credits: credits,
        price_per_period: prijs,
        credits_per_period: credits,
        initial_duration_months: durationMonths,
        is_rolling: isRolling,
        initial_end_date: initialEndDate,
        status: 'active',
        is_custom: isCustom,
        custom_price: isCustom && prijs !== staffelPrijs ? prijs : null,
        custom_credits: isCustom && credits !== typeCredits ? credits : null,
        payment_frequency: effectiveFreq,
        next_invoice_date: nieuwForm.start_date,
        price_per_month: staffel?.price_per_month ?? null,
        period_discount_pct: Number(discountPct),
        session_type: sTypeNieuw,
        pt_credits_per_month: sTypeNieuw === 'pt' ? creditsPerMonthNieuw : 0,
        pt_plus_credits_per_month: sTypeNieuw === 'pt_plus' ? creditsPerMonthNieuw : 0,
        small_group_credits_per_month: sTypeNieuw === 'small_group' ? creditsPerMonthNieuw : 0,
        current_pt_credits: sTypeNieuw === 'pt' ? creditsPerMonthNieuw : 0,
        current_pt_plus_credits: sTypeNieuw === 'pt_plus' ? creditsPerMonthNieuw : 0,
        current_small_group_credits: sTypeNieuw === 'small_group' ? creditsPerMonthNieuw : 0,
      };

      const { error: insErr } = await supabase.from('client_memberships').insert(insertData);
      if (insErr) throw insErr;

      await supabase.from('credit_transactions').insert({
        client_id: id,
        delta: credits,
        reason: 'grant_initial',
      });

      if (nieuwForm.welkomstmail) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            await fetch(`${supabase.supabaseUrl}/functions/v1/send-welcome-email`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ client_id: id }),
            });
          }
        } catch (_) { /* welkomstmail niet-fataal */ }
      }

      showSuccess(t('clientDetail.nieuwSucces'));
      setNieuwModal(false);
      setNieuwForm({ type_id: '', staffel_id: '', start_date: new Date().toISOString().slice(0, 10), prijs: '', credits: '', payment_frequency: '', welkomstmail: false, period_discount_pct: 0 });
      await load();
    } catch (err) {
      setNieuwError(t('clientDetail.nieuwFout') + err.message);
    } finally {
      setNieuwSaving(false);
    }
  }

  // ─── Actie 3: Historisch overzicht laden ─────────────────────
  async function loadAlleHistorie() {
    setHistorieLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('client_memberships')
        .select('id, status, start_date, end_date, cancellation_date, initial_duration_months, price_per_period, credits_per_period, is_custom, membership_types(name)')
        .eq('client_id', id)
        .order('start_date', { ascending: false });
      if (err) throw err;
      setAlleHistorie(data || []);
    } catch (_) { /* stil falen */ } finally {
      setHistorieLoading(false);
    }
  }

  async function handleUnblockClient() {
    setUnblocking(true);
    try {
      const result = await unblockClient(id, user.id);
      showSuccess(t('clientDetail.blokkeringOpgeheven'));
      setUnblockConfirm(false);
      load();
    } catch (err) {
      showError(t('clientDetail.blokkeringOpheffenFout') + err.message);
    } finally {
      setUnblocking(false);
    }
  }

  async function handleStuurWelkomstmail() {
    setStuuringWelkomstmail(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error(t('welkomstmail.foutSessie'));
      const resp = await fetch(`${supabase.supabaseUrl}/functions/v1/send-welcome-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: id }),
      });
      const result = await resp.json();
      if (!resp.ok || result.error) throw new Error(result.error || 'Onbekende fout');
      showSuccess(t('welkomstmail.succes'));
      setWelkomstmailConfirm(false);
    } catch (err) {
      showError(t('welkomstmail.fout') + err.message);
    } finally {
      setStuuringWelkomstmail(false);
    }
  }

  // BUG 16: Opnieuw sturen van bestaand token
  async function handleResendToken(pt) {
    setResendingTokenId(pt.id);
    try {
      const publicUrl = `${window.location.origin}/overeenkomst/${pt.token}`;

      // Haal overeenkomst-afzendadres op (email_agreements → fallback email_from)
      const { data: settingsRows } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['email_agreements', 'email_from']);
      const settingsMap = {};
      settingsRows?.forEach(({ key, value }) => { settingsMap[key] = value || ''; });
      const agreementFrom = settingsMap.email_agreements?.trim() || settingsMap.email_from?.trim() || '';

      const { error: emailErr } = await supabase.functions.invoke('send-email', {
        body: {
          to: client.email,
          from: agreementFrom || undefined,
          subject: 'Herinnering: Overeenkomst ondertekenen - Fit En Sterker',
          html: `
            <p>Beste ${client.full_name},</p>
            <p>We sturen je nogmaals de link om je overeenkomst te bekijken en digitaal te ondertekenen.</p>
            <p><a href="${publicUrl}" style="display:inline-block;background:#1d9e75;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:600;">Overeenkomst bekijken en ondertekenen</a></p>
            <p style="margin-top:20px;font-size:13px;color:#666;">Of kopieer deze link: <a href="${publicUrl}">${publicUrl}</a></p>
            <p>Met vriendelijke groet,<br>Fit En Sterker</p>
          `,
        },
      });
      if (emailErr) throw new Error(emailErr.message);
      // Log de herinnering in agreement_reminders zodat het in het communicatielogboek verschijnt
      await supabase.from('agreement_reminders').insert({
        client_id: client.id,
        token_id: pt.id,
        channel: 'email',
      });
      showSuccess(t('agreement.stuurOpnieuwSuccess'));
      await loadCommLog(true);
    } catch (err) {
      showError(t('agreement.stuurOpnieuwFout') + err.message);
    } finally {
      setResendingTokenId(null);
    }
  }

  async function handleSendProspectReminder() {
    setSendingProspectReminder(true);
    try {
      const { data: pushableToken } = await supabase.rpc('get_pushable_token', {
        p_user_id: client.id,
        p_category: 'agreements',
      });
      if (pushableToken) {
        await supabase.functions.invoke('send-push', {
          body: {
            tokens: [pushableToken],
            title: 'Herinnering proefsessie',
            body: `Hé ${client.full_name || 'daar'}! We herinneren je aan je proefsessie. Heb je al besloten of je lid wilt worden? 💪`,
            clientId: client.id,
          },
        });
      }
      const sentCount = commLog.filter(e => e.type === 'prospect_reminder').length;
      const { error: logErr } = await supabase.from('prospect_reminders').insert({
        prospect_id: client.id,
        reminder_number: sentCount + 1,
        sent_at: new Date().toISOString(),
        method: pushableToken ? 'push' : 'none',
        notes: 'Handmatig verstuurd via testmodus',
      });
      // K3-fix (2026-07-27): prospect_reminders kreeg een UNIQUE-index op
      // (prospect_id, reminder_number). Een 23505 hier betekent een race met
      // een gelijktijdige cron-run op hetzelfde volgnummer — de push is dan
      // al verstuurd, dus geen echte fout, alleen het logregel-nummer botst.
      if (logErr && logErr.code !== '23505') throw logErr;
      showSuccess(t('communication.prospectReminderVerstuurd'));
      await loadCommLog(true);
    } catch (err) {
      showError(t('communication.prospectReminderFout') + err.message);
    } finally {
      setSendingProspectReminder(false);
    }
  }

  // BUG 16: Link kopiëren naar klembord
  async function handleCopyLink(token, tokenId) {
    const publicUrl = `${window.location.origin}/overeenkomst/${token}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedTokenId(tokenId);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch {
      showError(t('clients.clipboardFout'));
    }
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);

      const [profileRes, membRes, membTypesRes, creditsRes, invoicesRes, settingsRes, enrollRes, ptRes, histRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, role, customer_number, street, house_number, postal_code, city, phone, home_location_id, client_status, blocked_at, blocked_by, deleted_at, locations(name)')
          .eq('id', id)
          .maybeSingle(),
        supabase
          .from('client_memberships')
          .select('id, current_credits, current_pt_credits, current_pt_plus_credits, current_small_group_credits, pt_credits_per_month, pt_plus_credits_per_month, small_group_credits_per_month, session_type, start_date, end_date, status, membership_type_id, is_custom, is_rolling, initial_duration_months, initial_end_date, price_per_period, price_per_month, payment_frequency, credits_per_period, cancellation_date, cancelled_by, last_credit_booking_at, reminder_days_override, custom_name, custom_price, custom_credits, custom_duration_days, signed_agreement_id, staffel_warning, payment_method, membership_types(id, name, legacy_credits_per_period, session_type, session_duration_minutes, pt_sessions_per_week, pt_plus_sessions_per_week, small_group_sessions_per_week, sessions_per_week_frequency)')
          .eq('client_id', id)
          .eq('status', 'active')
          .order('start_date', { ascending: false }),
        supabase
          .from('membership_types')
          .select('id, name, legacy_credits_per_period, session_type, pt_sessions_per_week, pt_plus_sessions_per_week, small_group_sessions_per_week, sessions_per_week_frequency, membership_type_periods(id, duration_months, price_per_month, price_per_period, vat_rate, available_from, available_until, payment_frequency, period_discount_pct, is_active)')
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('credit_transactions')
          .select('id, delta, reason, note, created_at, group_lessons(name, starts_at)')
          .eq('client_id', id)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('invoices')
          .select('id, invoice_number, subtotal, btw_amount, total_amount, amount_paid, status, created_at, period_start, period_end, is_credit_note, credit_note_for, invoice_reminders(type, sent_at, new_due_date)')
          .eq('client_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('settings')
          .select('key, value'),
        supabase
          .from('lesson_enrollments')
          .select('id, status, group_lessons(id, name, starts_at, type, location_id, locations(name))')
          .eq('client_id', id)
          .neq('status', 'cancelled')
          .order('id', { ascending: false }),
        supabase
          .from('group_lessons')
          .select('id, name, starts_at, type, location_id, locations(name)')
          .eq('client_id', id)
          .eq('type', 'personal')
          .order('starts_at', { ascending: false }),
        supabase
          .from('membership_history')
          .select('id, membership_type, start_date, end_date, created_at')
          .eq('client_id', id)
          .order('created_at', { ascending: false }),
      ]);

      if (profileRes.error) throw profileRes.error;
      if (!profileRes.data) throw new Error(t('clientDetail.clientNietGevonden'));

      if (role === 'coach') {
        const { data: link } = await supabase
          .from('coach_clients')
          .select('id')
          .eq('coach_id', user.id)
          .eq('client_id', id)
          .maybeSingle();
        if (!link) {
          navigate('/coach/clients');
          return;
        }
      }

      const clientData = profileRes.data;
      setClient(clientData);
      const allMemberships = membRes.data || [];
      setMemberships(allMemberships);
      setMembership(allMemberships[0] || null);
      setMembershipTypes(membTypesRes.data || []);
      setCredits(creditsRes.data || []);
      const rawInvoices = invoicesRes.data || [];
      const creditNoteForSet = new Set(
        rawInvoices.filter(i => i.credit_note_for).map(i => i.credit_note_for)
      );
      setInvoices(rawInvoices.map(i => ({
        ...i,
        has_credit_note: creditNoteForSet.has(i.id),
        latest_reminder: i.invoice_reminders?.length
          ? [...i.invoice_reminders].sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0]
          : null,
      })));

      const map = {};
      (settingsRes.data || []).forEach(({ key, value }) => { map[key] = value; });
      setCompany(map);

      // Combineer groepslessen + PT-afspraken, sorteer op datum.
      // Filter type='personal' uit enrollments: PT-lessen komen al via ptRes,
      // dubbele weergave wordt zo voorkomen.
      const groepslessen = (enrollRes.data || [])
        .filter(e => e.group_lessons && e.group_lessons.type !== 'personal')
        .map(e => ({
          id: e.id,
          naam: e.group_lessons.name,
          starts_at: e.group_lessons.starts_at,
          type: e.group_lessons.type,
          locatie: e.group_lessons.locations?.name || '—',
        }));
      const ptLessen = (ptRes.data || []).map(l => ({
        id: l.id,
        naam: l.name,
        starts_at: l.starts_at,
        type: 'personal',
        locatie: l.locations?.name || '—',
      }));
      const combined = [...groepslessen, ...ptLessen].sort(
        (a, b) => new Date(b.starts_at) - new Date(a.starts_at)
      );
      setAfspraken(combined);
      setMembershipHistory(histRes.data || []);

      // Overeenkomsten laden (ondertekend) — inclusief alle gekoppelde memberships via signed_agreement_id
      const agrRes = await supabase
        .from('signed_agreements')
        .select(`
          id,
          signed_at,
          pdf_url,
          membership_type:membership_types(name),
          client_memberships!signed_agreement_id(id, membership_types(name), initial_duration_months, payment_frequency, price_per_period, price_per_month, staffel_warning)
        `)
        .eq('client_id', id)
        .order('signed_at', { ascending: false });
      if (agrRes.error) throw agrRes.error;
      setAgreements(agrRes.data || []);

      // BUG 8: Openstaande agreement tokens laden
      const pendingRes = await supabase
        .from('agreement_tokens')
        .select('id, token, created_at, expires_at, membership_type:membership_types(name)')
        .eq('client_id', id)
        .is('used_at', null)
        .is('rejected_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });
      if (pendingRes.error) throw pendingRes.error;
      setPendingTokens(pendingRes.data || []);

      // BUG 18: Alle tokens laden (ook verlopen/afgewezen/ingetrokken)
      const allTokensRes = await supabase
        .from('agreement_tokens')
        .select('id, token, created_at, expires_at, used_at, rejected_at, rejection_reason, rejected_by, membership_type:membership_types(name)')
        .eq('client_id', id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (!allTokensRes.error) setAllTokens(allTokensRes.data || []);

      // Formulieren initialiseren
      setPersoonForm({
        full_name: clientData.full_name || '',
        phone: clientData.phone || '',
        street: clientData.street || '',
        house_number: clientData.house_number || '',
        postal_code: clientData.postal_code || '',
        city: clientData.city || '',
      });

      const memb = membRes.data;
      setMembForm({
        membership_type_id: memb?.membership_type_id || '',
        start_date: memb?.start_date || today,
        end_date: memb?.end_date || '',
        is_active: memb ? (!memb.end_date || memb.end_date >= today) : true,
      });

      // Laad custom velden indien bestaand custom abonnement
      if (memb?.is_custom) {
        setCustomEnabled(true);
        setCustomForm({
          custom_name: memb.custom_name || '',
          custom_price: memb.custom_price?.toString() || '',
          custom_credits: memb.custom_credits?.toString() || '',
          custom_duration_days: memb.custom_duration_days?.toString() || '',
          save_as_new_type: false,
        });
      }

      // F4-3: Laad membership reminder instelling
      const sysReminderSetting = map['reminder_days_before_prolongation'] || '14,7,3';
      setMembReminderSystemDefault(sysReminderSetting);
      if (memb) {
        if (memb.reminder_days_override === null || memb.reminder_days_override === undefined) {
          setMembReminderMode('system');
          const sysDays = sysReminderSetting.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
          setMembReminderDays({ 14: sysDays.includes(14), 7: sysDays.includes(7), 3: sysDays.includes(3) });
        } else if (memb.reminder_days_override === '') {
          setMembReminderMode('disabled');
          setMembReminderDays({ 14: false, 7: false, 3: false });
        } else {
          setMembReminderMode('custom');
          const customDays = memb.reminder_days_override.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
          setMembReminderDays({ 14: customDays.includes(14), 7: customDays.includes(7), 3: customDays.includes(3) });
        }
      }
    } catch (err) {
      setError(t('clientDetail.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadCommLog(reset = false) {
    const offset = reset ? 0 : commOffset;
    setCommLoading(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('get_client_communication_log', {
        p_client_id: id,
        p_limit: 21,
        p_offset: offset,
      });
      if (rpcErr) throw rpcErr;
      const rows = data || [];
      const hasMore = rows.length === 21;
      const slice = hasMore ? rows.slice(0, 20) : rows;
      if (reset) {
        setCommLog(slice);
        setCommOffset(slice.length);
        setExpandedRow(null);
      } else {
        setCommLog(prev => [...prev, ...slice]);
        setCommOffset(prev => prev + slice.length);
      }
      setCommHasMore(hasMore);
    } catch {
      // stil falen
    } finally {
      setCommLoading(false);
    }
  }

  async function loadLinesForInvoice(invoiceId) {
    if (invoiceLines[invoiceId]) return invoiceLines[invoiceId];
    const { data } = await supabase
      .from('invoice_lines')
      .select('id, description, quantity, unit_price, btw_percentage, amount')
      .eq('invoice_id', invoiceId)
      .order('id');
    const lines = data || [];
    setInvoiceLines(prev => ({ ...prev, [invoiceId]: lines }));
    return lines;
  }

  async function reloadInvoices() {
    const { data } = await supabase
      .from('invoices')
      .select('id, invoice_number, subtotal, btw_amount, total_amount, amount_paid, status, created_at, period_start, period_end, is_credit_note, credit_note_for, invoice_reminders(type, sent_at, new_due_date)')
      .eq('client_id', id)
      .order('created_at', { ascending: false });
    const rawInvoices = data || [];
    const creditNoteForSet = new Set(
      rawInvoices.filter(i => i.credit_note_for).map(i => i.credit_note_for)
    );
    setInvoices(rawInvoices.map(i => ({
      ...i,
      has_credit_note: creditNoteForSet.has(i.id),
      latest_reminder: i.invoice_reminders?.length
        ? [...i.invoice_reminders].sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0]
        : null,
    })));
  }


  async function handleDeleteInvoice(inv) {
    setActionLoading(inv.id);
    try {
      const { error: delErr } = await supabase.from('invoices').delete().eq('id', inv.id);
      if (delErr) throw delErr;
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
      showSuccess(t('facturen.verwijderenSucces'));
    } catch (err) {
      setError(t('facturen.foutVerwijderen') + err.message);
    } finally {
      setActionLoading(null);
      setDeleteConfirm(null);
    }
  }

  async function handleCreditnotaInvoice(inv) {
    setActionLoading(inv.id);
    try {
      const { data: creditId, error: rpcErr } = await supabase.rpc('create_credit_note', {
        p_invoice_id: inv.id,
      });
      if (rpcErr) throw rpcErr;
      archiveInvoicePaymentLink(inv.id, 'credit_note');

      let cnNummer = '';
      try {
        const { data: newInv } = await supabase
          .from('invoices')
          .select('invoice_number')
          .eq('id', creditId)
          .maybeSingle();
        cnNummer = newInv?.invoice_number || '';
      } catch { /* negeer */ }

      await reloadInvoices();
      showSuccess(`${t('facturen.creditnotaSucces')} ${cnNummer}`);
    } catch (err) {
      setError(t('facturen.foutCreditnota') + err.message);
    } finally {
      setActionLoading(null);
      setCreditnotaConfirm(null);
    }
  }

  async function handleIncasso(inv) {
    setActionLoading(inv.id);
    try {
      const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
        p_invoice_id: inv.id,
        p_type: 'collections',
        p_new_due_date: null,
      });
      if (rpcErr) throw rpcErr;
      showSuccess(t('facturen.incassoSucces'));
      reloadInvoices();
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setActionLoading(null);
      setIncassoConfirm(null);
    }
  }

  async function handleStatusChange(inv) {
    const nextStatus = VOLGENDE_STATUS[inv.status];
    if (!nextStatus) return;
    setUpdatingId(inv.id);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: nextStatus,
      });
      if (rpcErr) throw rpcErr;
      // F4-10 kritieke fix, Onderdeel 6: openstaande Payment Link
      // archiveren zodra een factuur handmatig op 'paid' wordt gezet —
      // niet blokkerend, alleen loggen bij falen.
      if (nextStatus === 'paid') {
        archiveInvoicePaymentLink(inv.id, 'manual_paid');
      }
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: nextStatus } : i));
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleMarkeerOnbetaald(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: 'sent',
      });
      if (rpcErr) throw rpcErr;
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
    } catch (err) {
      setError(t('facturen.foutOnbetaald') + err.message);
    } finally {
      setUpdatingId(null);
      setOnbetaaldConfirm(null);
    }
  }

  async function handleVolledigBetaald(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: 'paid',
      });
      if (rpcErr) throw rpcErr;
      archiveInvoicePaymentLink(inv.id, 'manual_paid');
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'paid' } : i));
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
      setVolledigBetaaldConfirm(null);
    }
  }

  function toggleTimeline(invoiceId) {
    setExpandedTimelines(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  }

  function handleCreditDone({ membershipId, poolColumn, nieuweBalans }) {
    setCreditModal(null);
    setMemberships(prev => prev.map(m => m.id === membershipId ? { ...m, [poolColumn]: nieuweBalans } : m));
    supabase
      .from('credit_transactions')
      .select('id, delta, reason, note, created_at, group_lessons(name, starts_at)')
      .eq('client_id', id)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => setCredits(data || []));
  }

  async function handlePersoonOpslaan(e) {
    e.preventDefault();
    if (client?.deleted_at) {
      setPersoonError(t('clientDetail.foutProfielVerwijderd'));
      return;
    }
    setPersoonSaving(true);
    setPersoonError('');
    try {
      const { error: updErr } = await supabase
        .from('profiles')
        .update({
          full_name: persoonForm.full_name,
          phone: persoonForm.phone || null,
          street: persoonForm.street || null,
          house_number: persoonForm.house_number || null,
          postal_code: persoonForm.postal_code || null,
          city: persoonForm.city || null,
          home_location_id: persoonForm.home_location_id || null,
        })
        .eq('id', id);
      if (updErr) throw updErr;
      showSuccess(t('clientDetail.persoonOpslaanSucces'));
      setPersoonEdit(false);
      await load();
    } catch (err) {
      setPersoonError(t('clientDetail.persoonOpslaanFout') + err.message);
    } finally {
      setPersoonSaving(false);
    }
  }

  async function handleMembOpslaan(e) {
    e.preventDefault();
    setMembSaving(true);
    setMembError('');
    const today = new Date().toISOString().slice(0, 10);
    try {
      // Validatie custom velden indien aangepaste voorwaarden
      if (customEnabled) {
        if (!customForm.custom_price || parseFloat(customForm.custom_price) < 0) {
          setMembError(t('clientDetail.customVoorwaardenPrijsVerplicht'));
          setMembSaving(false);
          return;
        }
        if (!customForm.custom_credits || parseInt(customForm.custom_credits) < 0) {
          setMembError(t('clientDetail.customVoorwaardenCreditsVerplicht'));
          setMembSaving(false);
          return;
        }
        if (!customForm.custom_duration_days || parseInt(customForm.custom_duration_days) <= 0) {
          setMembError(t('clientDetail.customVoorwaardenLooptijdVerplicht'));
          setMembSaving(false);
          return;
        }
      }

      // Indien "Opslaan als nieuw type" aangevinkt: eerst nieuw membership_type aanmaken
      let finalTypeId = membForm.membership_type_id;
      if (customEnabled && customForm.save_as_new_type) {
        const selectedType = membershipTypes.find(mt => mt.id === membForm.membership_type_id);
        const newTypeName = customForm.custom_name.trim() || selectedType?.name || 'Custom';
        const customDays = parseInt(customForm.custom_duration_days) || 31;
        const customMonths = customDays <= 31 ? 1 : customDays <= 93 ? 3 : customDays <= 186 ? 6 : 12;
        const { data: newType, error: typeErr } = await supabase
          .from('membership_types')
          .insert({
            name: newTypeName,
            legacy_credits_per_period: parseInt(customForm.custom_credits),
            is_active: true,
          })
          .select()
          .maybeSingle();
        if (typeErr) throw typeErr;
        // Voeg staffel toe voor het nieuwe type
        const customPrice = parseFloat(customForm.custom_price) || 0;
        await supabase.from('membership_type_periods').insert({
          membership_type_id: newType.id,
          duration_months: customMonths,
          price_per_period: customPrice,
          price_per_month: customMonths > 0 ? Math.round((customPrice / customMonths) * 100) / 100 : customPrice,
          is_active: true,
        });
        finalTypeId = newType.id;
      }

      if (membership?.id) {
        // Update bestaand abonnement
        const updateData = {
          membership_type_id: finalTypeId,
          start_date: membForm.start_date,
          end_date: membForm.end_date || null,
        };
        if (customEnabled && !customForm.save_as_new_type) {
          updateData.is_custom = true;
          updateData.custom_name = customForm.custom_name.trim() || null;
          updateData.custom_price = parseFloat(customForm.custom_price);
          updateData.custom_credits = parseInt(customForm.custom_credits);
          updateData.custom_duration_days = parseInt(customForm.custom_duration_days);
        } else if (!customEnabled) {
          // Reset custom velden als toggle uitstaat
          updateData.is_custom = false;
          updateData.custom_name = null;
          updateData.custom_price = null;
          updateData.custom_credits = null;
          updateData.custom_duration_days = null;
        }
        const { error: updErr } = await supabase
          .from('client_memberships')
          .update(updateData)
          .eq('id', membership.id);
        if (updErr) throw updErr;
      } else {
        // Insert nieuw abonnement
        const selectedType = membershipTypes.find(mt => mt.id === finalTypeId);
        const todayStr = new Date().toISOString().slice(0, 10);
        const defaultStaffel = (selectedType?.membership_type_periods || [])
          .filter(p => p.is_active && (p.available_from == null || p.available_from <= todayStr) && (p.available_until == null || p.available_until >= todayStr))
          .sort((a, b) => a.duration_months - b.duration_months)[0];
        const effectivePrice = customEnabled ? parseFloat(customForm.custom_price) : (defaultStaffel?.price_per_period || 0);
        const effectiveCredits = customEnabled ? parseInt(customForm.custom_credits) : (selectedType?.legacy_credits_per_period || 0);
        const durationMonths = customEnabled
          ? (() => { const d = parseInt(customForm.custom_duration_days) || 31; return d <= 31 ? 1 : d <= 93 ? 3 : d <= 186 ? 6 : 12; })()
          : (defaultStaffel?.duration_months || 1);
        const isRolling = durationMonths === 1;
        // Kalendermaand einddatum
        const insertEndDate = !isRolling && membForm.start_date
          ? (() => { const d = new Date(membForm.start_date + 'T00:00:00'); d.setMonth(d.getMonth() + durationMonths); return d.toISOString().split('T')[0]; })()
          : null;
        const insertData = {
          client_id: id,
          membership_type_id: finalTypeId,
          start_date: membForm.start_date,
          end_date: membForm.end_date || null,
          current_credits: effectiveCredits,
          price_per_period: effectivePrice,
          credits_per_period: effectiveCredits,
          initial_duration_months: durationMonths,
          is_rolling: isRolling,
          initial_end_date: insertEndDate,
          payment_frequency: defaultStaffel?.payment_frequency !== 'both' ? (defaultStaffel?.payment_frequency || 'period') : 'period',
          next_invoice_date: membForm.start_date || null,
          price_per_month: defaultStaffel?.price_per_month ?? null,
        };
        if (customEnabled && !customForm.save_as_new_type) {
          insertData.is_custom = true;
          insertData.custom_name = customForm.custom_name.trim() || null;
          insertData.custom_price = parseFloat(customForm.custom_price);
          insertData.custom_credits = parseInt(customForm.custom_credits);
          insertData.custom_duration_days = parseInt(customForm.custom_duration_days);
        }
        const { error: insErr } = await supabase
          .from('client_memberships')
          .insert(insertData);
        if (insErr) throw insErr;
      }
      showSuccess(t('clientDetail.lidmaatschapOpslaanSucces'));
      setMembEdit(false);
      setCustomEnabled(false);
      setCustomForm({
        custom_name: '',
        custom_price: '',
        custom_credits: '',
        custom_duration_days: '',
        save_as_new_type: false,
      });
      await load();
    } catch (err) {
      setMembError(t('clientDetail.lidmaatschapOpslaanFout') + err.message);
    } finally {
      setMembSaving(false);
    }
  }

  if (loading) return <div className="loading-center" style={{ height: '60vh' }}><div className="spinner" /></div>;
  if (error) return <div className="error-msg">{error}</div>;
  if (!client) return null;

  const isAdminRole = role === 'superadmin' || role === 'admin_coach';
  const termDays = parseInt(company.company_payment_term_days || '14');
  function isOverdue(inv) {
    if (!inv.created_at) return false;
    try { return addDays(parseISO(inv.created_at), termDays) < new Date(); }
    catch { return false; }
  }

  const active = membership && (!membership.end_date || membership.end_date >= new Date().toISOString().slice(0, 10));
  const eindDatum = calcEndDate(membership);
  const hasCollections = invoices.some(inv => inv.status === 'collections');
  const now = new Date().toISOString();
  const aankomend = afspraken.filter(a => a.starts_at >= now).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const geschiedenis = afspraken.filter(a => a.starts_at < now).sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
  const aankomendZichtbaar = showAllAankomend ? aankomend : aankomend.slice(0, 10);
  const geschiedenisZichtbaar = showAllGeschiedenis ? geschiedenis : geschiedenis.slice(0, 10);

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ marginBottom: 6 }}>
            <Link to="/coach/clients" className="text-muted" style={{ fontSize: 13 }}>
              {t('clientDetail.terugNaarClients')}
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 className="page-title" style={{ margin: 0 }}>{client.full_name || t('clientDetail.onbekend')}</h1>
            {client.client_status === 'prospect' && (
              <span style={{
                background: '#fbbf2422',
                color: '#d97706',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}>
                {t('prospect.badgeProspect')}
              </span>
            )}
            {client.blocked_at && (
              <span style={{
                background: '#dc262622',
                color: '#dc2626',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}>
                {t('clientDetail.badgeGeblokkeerd')}
              </span>
            )}
          </div>
          <p className="page-subtitle">{client.email}
            {client.customer_number && (
              <span style={{ marginLeft: 12, fontSize: 12, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                {client.customer_number}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!client.deleted_at && client.client_status === 'prospect' && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/coach/overeenkomst/opstellen/${client.id}`)}
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('prospect.overeenkomstOpstellenKnop')}
            </button>
          )}
          {/* F4-8: Blokkering opheffen knop */}
          {!client.deleted_at && client.blocked_at && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setUnblockConfirm(true)}
            >
              {t('clientDetail.blokkeringOpheffen')}
            </button>
          )}
          {/* Welkomstmail versturen */}
          {!client.deleted_at && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setWelkomstmailConfirm(true)}
            >
              {t('welkomstmail.stuurWelkomstmail')}
            </button>
          )}
          {/* BUG 13: Client verwijderen — alleen admin_coach en superadmin, niet als al verwijderd */}
          {isAdminRole && !client.deleted_at && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: '#dc2626', borderColor: '#dc2626', whiteSpace: 'nowrap' }}
              onClick={() => setDeleteClientConfirm(true)}
            >
              {t('clients.verwijderClient')}
            </button>
          )}
        </div>
      </div>

      {client.deleted_at && (
        <div style={{
          background: '#6b728022',
          border: '1px solid #6b7280',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 16,
          fontSize: 13,
          color: 'var(--color-text)',
        }}>
          {t('clientDetail.bannerProfielVerwijderd', { datum: fmtDate(client.deleted_at) })}
        </div>
      )}

      {hasCollections && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontWeight: 600, color: '#991b1b', fontSize: 14 }}>
            {t('clientDetail.bannierIncasso')}
          </span>
        </div>
      )}

      {/* Persoonsgegevens */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.persoonsgegevens')}</h2>
          {!persoonEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                setPersoonForm({
                  full_name: client.full_name || '',
                  phone: client.phone || '',
                  street: client.street || '',
                  house_number: client.house_number || '',
                  postal_code: client.postal_code || '',
                  city: client.city || '',
                  home_location_id: client.home_location_id || '',
                });
                setPersoonEdit(true);
                setPersoonError('');
              }}>
                {t('clientDetail.bewerken')}
              </button>
            </div>
          )}
        </div>
        {persoonError && <div className="error-msg" style={{ marginBottom: 12 }}>{persoonError}</div>}
        {persoonEdit ? (
          <form onSubmit={handlePersoonOpslaan}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelVolledigeNaam')}</label>
                <input className="form-input" value={persoonForm.full_name} onChange={e => setPersoonForm(p => ({ ...p, full_name: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelEmail')}</label>
                <input className="form-input" value={client.email} readOnly style={{ background: 'var(--color-bg)', cursor: 'default' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelTelefoon')}</label>
                <input className="form-input" value={persoonForm.phone} onChange={e => setPersoonForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelStandaardLocatie')}</label>
                <select className="form-input" value={persoonForm.home_location_id || ''} onChange={e => setPersoonForm(p => ({ ...p, home_location_id: e.target.value || null }))}>
                  <option value="">{t('clientDetail.geenLocatie')}</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelStraat')}</label>
                <input className="form-input" value={persoonForm.street} onChange={e => setPersoonForm(p => ({ ...p, street: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelHuisnummer')}</label>
                <input className="form-input" value={persoonForm.house_number} onChange={e => setPersoonForm(p => ({ ...p, house_number: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelPostcode')}</label>
                <input className="form-input" value={persoonForm.postal_code} onChange={e => setPersoonForm(p => ({ ...p, postal_code: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clientDetail.labelWoonplaats')}</label>
                <input className="form-input" value={persoonForm.city} onChange={e => setPersoonForm(p => ({ ...p, city: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" onClick={() => setPersoonEdit(false)}>{t('clientDetail.annuleren')}</button>
              <button type="submit" className="btn btn-primary" disabled={persoonSaving}>
                {persoonSaving ? '…' : t('clientDetail.opslaan')}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <Row label={t('clientDetail.labelVolledigeNaam')} value={client.full_name || '—'} />
            <Row label={t('clientDetail.labelEmail')} value={client.email || '—'} />
            {client.phone && <Row label={t('clientDetail.labelTelefoon')} value={client.phone} />}
            <Row label={t('clientDetail.labelStandaardLocatie')} value={client.locations?.name || t('clientDetail.geenLocatie')} />
            {(client.street || client.house_number) && (
              <Row label={t('clientDetail.labelStraat')} value={[client.street, client.house_number].filter(Boolean).join(' ')} />
            )}
            {(client.postal_code || client.city) && (
              <Row label={t('clientDetail.labelPostcode')} value={[client.postal_code, client.city].filter(Boolean).join(' ')} />
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Lidmaatschap */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.lidmaatschap')}</h2>
            <button className="btn btn-primary btn-sm" onClick={() => {
              setNieuwForm({ type_id: '', staffel_id: '', start_date: new Date().toISOString().slice(0, 10), prijs: '', credits: '', welkomstmail: !membership });
              setNieuwStaffels([]);
              setNieuwError('');
              setNieuwModal(true);
            }}>
              {t('clientDetail.lidmaatschapNieuw')}
            </button>
          </div>
          {membError && <div className="error-msg" style={{ marginBottom: 12 }}>{membError}</div>}
          {memberships.length > 0 ? memberships.map((memb, mIdx) => {
            const typeName = memb.custom_name || memb.membership_types?.name || '—';
            const duurLabel = memb.initial_duration_months
              ? `${memb.initial_duration_months} maand${memb.initial_duration_months !== 1 ? 'en' : ''}`
              : '—';
            const statusLabel = memb.status === 'active' ? t('clientDetail.actief') : t('clientDetail.verlopen');
            const isActief = memb.status === 'active';
            const laatsVerlenging = memb.last_credit_booking_at
              ? fmtDate(memb.last_credit_booking_at.slice(0, 10))
              : t('clientDetail.nogNietVerlengd');
            const anker = memb.last_credit_booking_at
              ? new Date(memb.last_credit_booking_at)
              : new Date(memb.start_date + 'T00:00:00');
            const volgende = new Date(anker.getTime() + 30 * 86400000).toISOString().slice(0, 10);
            return (
              <div key={memb.id} style={{ display: 'grid', gap: 10, marginBottom: mIdx < memberships.length - 1 ? 20 : 0, paddingBottom: mIdx < memberships.length - 1 ? 20 : 0, borderBottom: mIdx < memberships.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                <Row label={t('clientDetail.type')} value={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong>{typeName}</strong>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>— {duurLabel}</span>
                    {memb.is_custom && (
                      <span className="badge" style={{ background: 'var(--color-warning)', color: '#fff', fontSize: 11, padding: '2px 8px' }}>
                        {t('clientDetail.badgeAangepast')}
                      </span>
                    )}
                  </div>
                } />
                <Row label={t('clientDetail.status')} value={
                  <span className={`badge ${isActief ? 'badge-active' : 'badge-inactive'}`}>{statusLabel}</span>
                } />
                <Row label={t('clientDetail.startdatum')} value={fmtDate(memb.start_date)} />
                {memb.initial_end_date && (
                  <Row label={t('clientDetail.initialEndDate')} value={fmtDate(memb.initial_end_date)} />
                )}
                <Row label={
                  memb.payment_frequency === 'month'
                    ? t('clientDetail.pricePerMonth')
                    : t('clientDetail.pricePerPeriod')
                } value={
                  memb.payment_frequency === 'month'
                    ? `${euro(memb.price_per_month || 0)}/mnd`
                    : euro(memb.price_per_period || 0)
                } />
                <Row label={t('clientDetail.betaalmethodeLabel')} value={
                  pmChangeTarget === memb.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <select
                        value={pmChangeValue}
                        onChange={e => setPmChangeValue(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)' }}
                      >
                        <option value="manual">{t('clients.betaalmethodenLabelHandmatig')}</option>
                        {computeAllowedPaymentMethods().includes('ideal') && (
                          <option value="ideal">{t('clients.betaalmethodenLabelIdeal')}</option>
                        )}
                        {computeAllowedPaymentMethods().includes('sepa') && (
                          <option value="sepa">{t('clients.betaalmethodenLabelSepa')}</option>
                        )}
                      </select>
                      {pmChangeValue === 'sepa' && (
                        <div style={{ fontSize: 12, color: 'var(--color-warning)' }}>
                          {t('clientDetail.betaalmethodeSepaHint')}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={pmChangeSaving || pmChangeValue === 'sepa' || pmChangeValue === memb.payment_method}
                          onClick={() => handleConfirmChangePaymentMethod(memb.id)}
                        >
                          {pmChangeSaving ? t('common.saving') : t('clientDetail.betaalmethodeBevestigen')}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={pmChangeSaving}
                          onClick={() => setPmChangeTarget(null)}
                        >
                          {t('clientDetail.betaalmethodeAnnuleren')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span>
                        {memb.payment_method === 'ideal' ? t('clients.betaalmethodenLabelIdeal')
                          : memb.payment_method === 'sepa' ? t('clients.betaalmethodenLabelSepa')
                          : t('clients.betaalmethodenLabelHandmatig')}
                      </span>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setPmChangeTarget(memb.id); setPmChangeValue(memb.payment_method || 'manual'); }}
                      >
                        {t('clientDetail.betaalmethodeWijzigen')}
                      </button>
                    </div>
                  )
                } />
                {memb.staffel_warning && (
                  <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#92400e', marginTop: 4 }}>
                    ⚠️ {memb.staffel_warning}
                  </div>
                )}
                {memb.pt_credits_per_month > 0 && (
                  <Row label={t('clientDetail.ptCreditsLabel')} value={
                    <span>
                      <strong style={{ color: 'var(--color-primary)' }}>{memb.current_pt_credits || 0}</strong>
                      <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                        ({memb.membership_types?.pt_sessions_per_week || 0}×/wk × 4,33 = {memb.pt_credits_per_month}/mnd)
                      </span>
                    </span>
                  } />
                )}
                {memb.pt_plus_credits_per_month > 0 && (
                  <Row label={t('clientDetail.ptPlusCreditsLabel')} value={
                    <span>
                      <strong style={{ color: 'var(--color-primary)' }}>{memb.current_pt_plus_credits || 0}</strong>
                      <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                        ({memb.membership_types?.pt_plus_sessions_per_week || 0}×/wk × 4,33 = {memb.pt_plus_credits_per_month}/mnd)
                      </span>
                    </span>
                  } />
                )}
                {memb.small_group_credits_per_month > 0 && (
                  <Row label={t('clientDetail.smallGroupCreditsLabel')} value={
                    <span>
                      <strong style={{ color: 'var(--color-primary)' }}>{memb.current_small_group_credits || 0}</strong>
                      <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                        ({memb.membership_types?.small_group_sessions_per_week || 0}×/wk × 4,33 = {memb.small_group_credits_per_month}/mnd)
                      </span>
                    </span>
                  } />
                )}
                {!memb.pt_credits_per_month && !memb.pt_plus_credits_per_month && !memb.small_group_credits_per_month && (
                  <>
                    <Row label={t('clientDetail.creditsPerPeriod')} value={memb.credits_per_period ?? '—'} />
                    <Row label={t('clientDetail.creditsResterend')} value={
                      <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-primary)' }}>{memb.current_credits}</span>
                    } />
                  </>
                )}
                <Row label={t('clientDetail.laatsVerlenging')} value={laatsVerlenging} />
                {isActief && !memb.cancellation_date && (
                  <Row label={t('clientDetail.volgendeVerlenging')} value={fmtDate(volgende)} />
                )}
                {memb.cancellation_date && (
                  <Row label={t('clientDetail.opzegdatum')} value={
                    <span style={{ fontWeight: 600, color: '#92400e' }}>{fmtDate(memb.cancellation_date)}</span>
                  } />
                )}
                {isActief && !memb.cancellation_date && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                      onClick={() => {
                        setBeeindigenOptie('einde_periode');
                        setBeeindigenDatum('');
                        setBeeindigenError('');
                        setBeeindigenModal({ membershipId: memb.id });
                      }}
                    >
                      {t('clientDetail.lidmaatschapBeeindigen')}
                    </button>
                  </div>
                )}
              </div>
            );
          }) : (
            <p className="text-muted">{t('clientDetail.geenLidmaatschap')}</p>
          )}
        </div>

        {/* F4-3: Herinneringen */}
        {membership && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('clientDetail.membReminderTitel')}</h2>
            <p className="text-muted" style={{ marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
              {t('clientDetail.membReminderHint')}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                <input type="radio" name="memb-reminder-mode" checked={membReminderMode === 'system'}
                  onChange={() => {
                    setMembReminderMode('system');
                    const sysDays = membReminderSystemDefault.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
                    setMembReminderDays({ 14: sysDays.includes(14), 7: sysDays.includes(7), 3: sysDays.includes(3) });
                  }}
                  style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clientDetail.membReminderSysteem')} ({membReminderSystemDefault})</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                <input type="radio" name="memb-reminder-mode" checked={membReminderMode === 'custom'}
                  onChange={() => setMembReminderMode('custom')}
                  style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clientDetail.membReminderAangepast')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="radio" name="memb-reminder-mode" checked={membReminderMode === 'disabled'}
                  onChange={() => {
                    setMembReminderMode('disabled');
                    setMembReminderDays({ 14: false, 7: false, 3: false });
                  }}
                  style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clientDetail.membReminderUitgeschakeld')}</span>
              </label>
            </div>

            {membReminderMode === 'custom' && (
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, paddingLeft: 26 }}>
                {[14, 7, 3].map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={membReminderDays[d] || false}
                      onChange={e => setMembReminderDays(prev => ({ ...prev, [d]: e.target.checked }))}
                      style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 14 }}>{d} {t('clientDetail.membReminderDagen')}</span>
                  </label>
                ))}
              </div>
            )}

            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.5, fontStyle: 'italic' }}>
              {t('clientDetail.membReminderWettelijk')}
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSaveMembReminder} disabled={membReminderSaving}>
                {membReminderSaving ? t('auth.loading') : t('clientDetail.opslaan')}
              </button>
            </div>
          </div>
        )}

        {/* Overzicht */}
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{t('clientDetail.overzicht')}</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            <Row label={t('clientDetail.klantnummer')} value={client.customer_number || '—'} />
            <Row label={t('clientDetail.creditbewegingen')} value={`${credits.length} ${t('clientDetail.last20')}`} />
            <Row label={t('clientDetail.facturen')} value={`${invoices.length} ${t('clientDetail.facturenTotaal')}`} />
            <Row
              label={t('clientDetail.openstaand')}
              value={`${invoices.filter(i => i.status === 'draft' || i.status === 'sent').length} ${t('clientDetail.facturenLabel')}`}
            />
          </div>
        </div>
      </div>

      {/* Afspraken sectie */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.afspraken')}</h2>
          <button
            className="btn btn-sm"
            onClick={() => navigate('/coach/les/nieuw', {
              state: {
                prefillPT: true,
                type: 'personal',
                clientId: client.id,
                coachId: user.id,
                clientName: client.full_name,
              }
            })}
            style={{ fontSize: 13 }}
          >
            {t('clientDetail.ptAfspraakInplannen')}
          </button>
        </div>

        {/* Aankomend */}
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)', marginBottom: 10 }}>{t('clientDetail.afsprakenAankomend')}</h3>
        {aankomend.length === 0 ? (
          <p className="text-muted" style={{ marginBottom: 20 }}>{t('clientDetail.geenAankomend')}</p>
        ) : (
          <>
            <div className="table-wrap" style={{ marginBottom: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>{t('clientDetail.colAfspraakNaam')}</th>
                    <th>{t('clientDetail.colAfspraakDatum')}</th>
                    <th>{t('clientDetail.colAfspraakType')}</th>
                    <th>{t('clientDetail.colAfspraakLocatie')}</th>
                    <th>{t('clientDetail.colAfspraakStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {aankomendZichtbaar.map(a => (
                    <tr key={a.id + a.type}>
                      <td className="font-medium">{a.naam}</td>
                      <td className="text-muted">{fmtLesDateTime(a.starts_at)}</td>
                      <td>
                        <span className={`badge ${a.type === 'personal' ? 'badge-inactive' : 'badge-active'}`}>
                          {a.type === 'personal' ? t('clientDetail.badgePT') : a.type === 'workshop' ? t('clientDetail.badgeWorkshop') : t('clientDetail.badgeGroepsles')}
                        </span>
                      </td>
                      <td className="text-muted">{a.locatie}</td>
                      <td>
                        <span className="badge badge-active">{t('clientDetail.badgeAankomend')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {aankomend.length > 10 && (
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAllAankomend(v => !v)}>
                  {showAllAankomend ? t('clientDetail.toonMinder') : t('clientDetail.toonMeer')}
                </button>
              </div>
            )}
          </>
        )}

        <div style={{ borderTop: '1px solid var(--color-border)', marginTop: aankomend.length === 0 ? 0 : 16, marginBottom: 16 }} />

        {/* Geschiedenis */}
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 10 }}>{t('clientDetail.afsprakenGeschiedenis')}</h3>
        {geschiedenis.length === 0 ? (
          <p className="text-muted">{t('clientDetail.geenGeschiedenisTabel')}</p>
        ) : (
          <>
            <div className="table-wrap" style={{ marginBottom: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>{t('clientDetail.colAfspraakNaam')}</th>
                    <th>{t('clientDetail.colAfspraakDatum')}</th>
                    <th>{t('clientDetail.colAfspraakType')}</th>
                    <th>{t('clientDetail.colAfspraakLocatie')}</th>
                    <th>{t('clientDetail.colAfspraakStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {geschiedenisZichtbaar.map(a => (
                    <tr key={a.id + a.type}>
                      <td className="font-medium">{a.naam}</td>
                      <td className="text-muted">{fmtLesDateTime(a.starts_at)}</td>
                      <td>
                        <span className={`badge ${a.type === 'personal' ? 'badge-inactive' : 'badge-active'}`}>
                          {a.type === 'personal' ? t('clientDetail.badgePT') : a.type === 'workshop' ? t('clientDetail.badgeWorkshop') : t('clientDetail.badgeGroepsles')}
                        </span>
                      </td>
                      <td className="text-muted">{a.locatie}</td>
                      <td>
                        <span className="badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>{t('clientDetail.badgeGevolgd')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {geschiedenis.length > 10 && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAllGeschiedenis(v => !v)}>
                  {showAllGeschiedenis ? t('clientDetail.toonMinder') : t('clientDetail.toonMeer')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Credits sectie — alle actieve pools naast elkaar */}
      {(() => {
        // Bouw lijst van actieve creditpools op basis van alle actieve abonnementen
        const pools = [];
        for (const memb of memberships) {
          const dur = memb.membership_types?.session_duration_minutes;
          if (memb.session_type === 'pt') {
            pools.push({ key: 'pt', label: t('clientDetail.poolPT'), balance: memb.current_pt_credits ?? 0 });
          } else if (memb.session_type === 'pt_plus' && dur === 60) {
            pools.push({ key: 'pt_plus_60', label: t('clientDetail.poolPTPlus60'), balance: memb.current_pt_plus_credits ?? 0 });
          } else if (memb.session_type === 'pt_plus' && dur === 30) {
            pools.push({ key: 'pt_plus_30', label: t('clientDetail.poolPTPlus30'), balance: memb.current_pt_plus_credits ?? 0 });
          } else if (memb.session_type === 'pt_plus') {
            pools.push({ key: 'pt_plus', label: t('clientDetail.poolPTPlus60'), balance: memb.current_pt_plus_credits ?? 0 });
          } else if (memb.session_type === 'small_group') {
            pools.push({ key: 'small_group', label: t('clientDetail.poolSmallGroup'), balance: memb.current_small_group_credits ?? 0 });
          }
        }
        // Aggregeer per pool: som balansen van alle actieve abonnementen binnen dezelfde pool
        const seen = {};
        const dedupedPools = [];
        for (const pool of pools) {
          if (!seen[pool.key]) {
            seen[pool.key] = { ...pool };
          } else {
            seen[pool.key] = { ...seen[pool.key], balance: seen[pool.key].balance + pool.balance };
          }
        }
        for (const key of ['pt', 'pt_plus_60', 'pt_plus_30', 'pt_plus', 'small_group']) {
          if (seen[key]) dedupedPools.push(seen[key]);
        }
        const colCount = Math.max(1, dedupedPools.length);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.creditsTitel')}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setCreditModal('bij')}>
                  {t('clientDetail.creditsBijschrijven')}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setCreditModal('af')}>
                  {t('clientDetail.creditsAfschrijven')}
                </button>
              </div>
            </div>
            {dedupedPools.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: 0, textAlign: 'center', padding: '12px 0' }}>
                {t('clientDetail.geenCreditPools')}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${colCount}, 1fr)`, gap: 12 }}>
                {dedupedPools.map(pool => (
                  <div key={pool.key} style={{ textAlign: 'center', padding: '12px 8px', background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 8 }}>
                    <div style={{ fontSize: 40, fontWeight: 700, color: 'var(--color-primary)', lineHeight: 1 }}>
                      {pool.balance}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>
                      {pool.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Creditbewegingen */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
          {t('clientDetail.creditbewegingenTitel')}
        </h2>
        {credits.length === 0 ? (
          <p className="text-muted">{t('clientDetail.geenTransacties')}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clientDetail.colDatum')}</th>
                  <th>{t('clientDetail.colMutatie')}</th>
                  <th>{t('clientDetail.colReden')}</th>
                  <th>{t('clientDetail.colNotitie')}</th>
                </tr>
              </thead>
              <tbody>
                {credits.map(ct => (
                  <tr key={ct.id}>
                    <td className="text-muted">{fmtDateTime(ct.created_at)}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: ct.delta > 0 ? 'var(--color-primary)' : 'var(--color-danger)' }}>
                        {ct.delta > 0 ? '+' : ''}{ct.delta}
                      </span>
                    </td>
                    <td>{fmtCreditReden(ct)}</td>
                    <td className="text-muted">{ct.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lidmaatschapsgeschiedenis */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
          {t('clientDetail.lidmaatschapGeschiedenisTitel')}
        </h2>
        {membershipHistory.length === 0 ? (
          <p className="text-muted">{t('clientDetail.lidmaatschapGeenGeschiedenis')}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clientDetail.colHistDatum')}</th>
                  <th>{t('clientDetail.colHistType')}</th>
                  <th>{t('clientDetail.colHistStartdatum')}</th>
                  <th>{t('clientDetail.colHistEinddatum')}</th>
                </tr>
              </thead>
              <tbody>
                {membershipHistory.map(h => (
                  <tr key={h.id}>
                    <td className="text-muted">{fmtDate(h.created_at)}</td>
                    <td className="font-medium">{h.membership_type || '—'}</td>
                    <td className="text-muted">{fmtDate(h.start_date)}</td>
                    <td className="text-muted">{fmtDate(h.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Voeding & Kcal */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('voeding.titel')}</h2>
          <button className="btn btn-secondary btn-sm" onClick={() => setKcalModal(true)}>
            {t('voeding.instellen')}
          </button>
        </div>
        {kcalLoading ? (
          <p className="text-muted">{t('common.laden')}</p>
        ) : kcalAssignment ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('voeding.dagKcal')}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-primary)' }}>{kcalAssignment.daily_calories}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>kcal</div>
            </div>
            {kcalAssignment.protein_target != null && (
              <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('voeding.eiwitten')}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{kcalAssignment.protein_target}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>gram</div>
              </div>
            )}
            {kcalAssignment.carbs_target != null && (
              <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('voeding.koolhydraten')}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{kcalAssignment.carbs_target}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>gram</div>
              </div>
            )}
            {kcalAssignment.fat_target != null && (
              <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('voeding.vetten')}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{kcalAssignment.fat_target}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>gram</div>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
              {t('voeding.ingesteldOp')} {fmtDate(kcalAssignment.created_at)}
            </div>
          </div>
        ) : (
          <p className="text-muted">{t('voeding.geenToewijzing')}</p>
        )}
      </div>

      {/* E-mail automatisering */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('clients.emailAutomatiseringTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          {t('clients.emailAutomatiseringHint')}
        </p>

        {/* Radio: Systeem / Aangepast */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="radio"
              name="email-auto-mode"
              checked={emailAutoMode === 'system'}
              onChange={() => {
                setEmailAutoMode('system');
                setEmailAutoSettings({
                  auto_reminder: emailAutoSystemDefaults.email_auto_reminder === 'true',
                  auto_final_notice: emailAutoSystemDefaults.email_auto_final_notice === 'true',
                });
              }}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.emailAutomatiseringRadioSysteem')}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="radio"
              name="email-auto-mode"
              checked={emailAutoMode === 'custom'}
              onChange={() => setEmailAutoMode('custom')}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.emailAutomatiseringRadioAangepast')}</span>
          </label>
        </div>

        {/* Custom toggles (alleen zichtbaar bij "Aangepast") */}
        {emailAutoMode === 'custom' && (
          <div style={{ display: 'grid', gap: 16, marginBottom: 20, paddingLeft: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="checkbox"
                id="client_auto_reminder"
                checked={emailAutoSettings.auto_reminder}
                onChange={e => setEmailAutoSettings(p => ({ ...p, auto_reminder: e.target.checked }))}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="client_auto_reminder" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }}>
                {t('clients.emailAutomatiseringLabelReminder')}
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="checkbox"
                id="client_auto_final_notice"
                checked={emailAutoSettings.auto_final_notice}
                onChange={e => setEmailAutoSettings(p => ({ ...p, auto_final_notice: e.target.checked }))}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="client_auto_final_notice" style={{ fontSize: 14, cursor: 'pointer', margin: 0 }}>
                {t('clients.emailAutomatiseringLabelFinalNotice')}
              </label>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSaveEmailAuto} disabled={emailAutoSaving}>
            {emailAutoSaving ? t('auth.loading') : t('clients.emailAutomatiseringOpslaan')}
          </button>
        </div>

        {/* UITBREIDING 9: Overeenkomst herinnering override */}
        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--color-border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            {t('clients.agrReminderTitel')}
          </h3>
          <p className="text-muted" style={{ marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
            {t('clients.agrReminderHint')}
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
              <input
                type="radio"
                name="agr-reminder-mode"
                checked={agrReminderMode === 'system'}
                onChange={() => setAgrReminderMode('system')}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.emailAutomatiseringRadioSysteem')}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="radio"
                name="agr-reminder-mode"
                checked={agrReminderMode === 'custom'}
                onChange={() => setAgrReminderMode('custom')}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.emailAutomatiseringRadioAangepast')}</span>
            </label>
          </div>

          {agrReminderMode === 'custom' && (
            <div style={{ paddingLeft: 26, display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="checkbox"
                  id="agr_reminder_enabled"
                  checked={agrReminderSettings.enabled}
                  onChange={e => setAgrReminderSettings(p => ({ ...p, enabled: e.target.checked }))}
                  style={{ width: 16, height: 16 }}
                />
                <label htmlFor="agr_reminder_enabled" style={{ fontSize: 14, margin: 0 }}>
                  {t('clients.agrReminderEnabledLabel')}
                </label>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {t('clients.agrReminderIntervalLabel')}
                </label>
                <input
                  type="number"
                  className="form-input"
                  value={agrReminderSettings.interval_days}
                  onChange={e => setAgrReminderSettings(p => ({ ...p, interval_days: parseInt(e.target.value) || 1 }))}
                  min="1"
                  style={{ maxWidth: 100 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {t('clients.agrReminderMaxLabel')}
                </label>
                <input
                  type="number"
                  className="form-input"
                  value={agrReminderSettings.max_reminders}
                  onChange={e => setAgrReminderSettings(p => ({ ...p, max_reminders: parseInt(e.target.value) || 0 }))}
                  min="0"
                  style={{ maxWidth: 100 }}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSaveAgrReminder} disabled={agrReminderSaving}>
              {agrReminderSaving ? t('auth.loading') : t('clients.emailAutomatiseringOpslaan')}
            </button>
          </div>
        </div>
      </div>

      {/* F4-10: toegestane betaalmethoden voor deze client */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('clients.betaalmethodenTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          {t('clients.betaalmethodenHint')}
        </p>

        {!pmSystem.ideal && !pmSystem.sepa ? (
          <p className="text-muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
            {t('clients.betaalmethodenGeenSysteem')}
          </p>
        ) : (
          <>
            {/* Radio: geen restrictie / aangepast */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="pm-mode"
                  checked={pmMode === 'system'}
                  onChange={() => setPmMode('system')}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.betaalmethodenRadioAlle')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="pm-mode"
                  checked={pmMode === 'custom'}
                  onChange={() => setPmMode('custom')}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('clients.betaalmethodenRadioAangepast')}</span>
              </label>
            </div>

            {pmMode === 'custom' && (
              <div style={{ display: 'grid', gap: 16, marginBottom: 20, paddingLeft: 26 }}>
                {/* Handmatig: altijd beschikbaar, informatief */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input type="checkbox" checked disabled style={{ width: 18, height: 18 }} />
                  <label style={{ fontSize: 14, margin: 0, color: 'var(--color-text-muted)' }}>
                    {t('clients.betaalmethodenLabelHandmatig')}
                  </label>
                </div>
                {/* iDEAL */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} title={!pmSystem.ideal ? t('clients.betaalmethodenTooltipUit') : undefined}>
                  <input
                    type="checkbox"
                    id="pm_client_ideal"
                    checked={pmSystem.ideal && pmClient.ideal}
                    disabled={!pmSystem.ideal}
                    onChange={e => setPmClient(p => ({ ...p, ideal: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: pmSystem.ideal ? 'pointer' : 'not-allowed' }}
                  />
                  <label htmlFor="pm_client_ideal" style={{ fontSize: 14, margin: 0, cursor: pmSystem.ideal ? 'pointer' : 'default', color: pmSystem.ideal ? 'inherit' : 'var(--color-text-muted)' }}>
                    {t('clients.betaalmethodenLabelIdeal')}
                    {!pmSystem.ideal && <span style={{ fontStyle: 'italic' }}> — {t('clients.betaalmethodenSysteemUit')}</span>}
                  </label>
                </div>
                {/* SEPA */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} title={!pmSystem.sepa ? t('clients.betaalmethodenTooltipUit') : undefined}>
                  <input
                    type="checkbox"
                    id="pm_client_sepa"
                    checked={pmSystem.sepa && pmClient.sepa}
                    disabled={!pmSystem.sepa}
                    onChange={e => setPmClient(p => ({ ...p, sepa: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: pmSystem.sepa ? 'pointer' : 'not-allowed' }}
                  />
                  <label htmlFor="pm_client_sepa" style={{ fontSize: 14, margin: 0, cursor: pmSystem.sepa ? 'pointer' : 'default', color: pmSystem.sepa ? 'inherit' : 'var(--color-text-muted)' }}>
                    {t('clients.betaalmethodenLabelSepa')}
                    {!pmSystem.sepa && <span style={{ fontStyle: 'italic' }}> — {t('clients.betaalmethodenSysteemUit')}</span>}
                  </label>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSavePaymentMethods} disabled={pmSaving}>
                {pmSaving ? t('auth.loading') : t('clients.betaalmethodenOpslaan')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Testmodus toggle — alleen voor superadmin en admin_coach */}
      {isAdminRole && (
        <div style={{
          background: '#fef9c3',
          border: '1px solid #fcd34d',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', margin: 0 }}>
            <input
              type="checkbox"
              checked={testmodus}
              onChange={e => setTestmodus(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            {t('facturen.testmodusLabel')}
          </label>
        </div>
      )}

      {/* Facturen */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.facturen')}</h2>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowNieuweFactuur(true)}>
            {t('clientDetail.nieuweFactuur')}
          </button>
        </div>
        {invoices.length === 0 ? (
          <p className="text-muted">{t('clientDetail.geenFacturen')}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clientDetail.colNummer')}</th>
                  <th>{t('clientDetail.colPeriode')}</th>
                  <th>{t('clientDetail.colTotaal')}</th>
                  <th>{t('clientDetail.colStatus')}</th>
                  <th>{t('clientDetail.colDatum')}</th>
                  <th style={{ textAlign: 'right' }}>{t('facturen.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const invoiceMap = Object.fromEntries(invoices.map(i => [i.id, i.invoice_number]));
                  return invoices.map(inv => {
                    const showReminderBtn = inv.status === 'sent' && !inv.is_credit_note && (testmodus || isOverdue(inv));
                    const showFinalBtn    = inv.status === 'reminded' && !inv.is_credit_note
                      && (testmodus || Math.floor((Date.now() - new Date(inv.latest_reminder?.sent_at).getTime()) / 86400000) >= 8);
                    const showIncassoBtn  = inv.status === 'final_notice' && !inv.is_credit_note
                      && (testmodus || isAdminRole || Math.floor((Date.now() - new Date(inv.latest_reminder?.sent_at).getTime()) / 86400000) >= 15);
                    const hasTimeline = (inv.invoice_reminders?.length ?? 0) > 0;

                    return (
                      <React.Fragment key={inv.id}>
                        <tr>
                          <td className="font-medium">
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {inv.invoice_number}
                              {inv.is_credit_note && (
                                <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#fff7ed', color: '#c2410c', fontWeight: 600 }}>
                                  {t('facturen.badgeCreditnota')}
                                </span>
                              )}
                            </div>
                            {inv.is_credit_note && inv.credit_note_for && (
                              <div className="text-sm text-muted">
                                {t('facturen.bijFactuur')} {invoiceMap[inv.credit_note_for] || '—'}
                              </div>
                            )}
                          </td>
                          <td className="text-muted">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                          <td>
                            {euro(inv.total_amount)}
                            {inv.status === 'partially_paid' && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                                {t('facturen.betaaldInfoLabel')} {euro(inv.amount_paid || 0)} — {t('facturen.openstaandInfoLabel')} {euro((inv.total_amount || 0) - (inv.amount_paid || 0))}
                              </div>
                            )}
                          </td>
                          <td><StatusBadge status={inv.status} /></td>
                          <td className="text-muted">{fmtDate(inv.created_at)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              {/* Status knop — reguliere facturen */}
                              {VOLGENDE_STATUS[inv.status] && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => {
                                    if (VOLGENDE_STATUS[inv.status] === 'paid') {
                                      setBetaaldConfirm(inv);
                                    } else {
                                      handleStatusChange(inv);
                                    }
                                  }}
                                  disabled={updatingId === inv.id || actionLoading === inv.id}
                                >
                                  {updatingId === inv.id ? '…' : VOLGENDE_LABEL[inv.status]?.() ?? ''}
                                </button>
                              )}
                              {/* Markeer betaald voor creditnota's (5C) */}
                              {inv.is_credit_note && inv.status === 'sent' && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaald')}
                                </button>
                              )}
                              {/* Markeer onbetaald (5B) */}
                              {inv.status === 'paid' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  disabled={updatingId === inv.id}
                                  onClick={() => setOnbetaaldConfirm(inv)}
                                >
                                  {t('facturen.onbetaaldKnop')}
                                </button>
                              )}
                              <PdfButton
                                inv={{ ...inv, client }}
                                onLoadLines={loadLinesForInvoice}
                                lines={invoiceLines[inv.id]}
                                company={company}
                              />
                              {showReminderBtn && (
                                <button
                                  className="btn btn-sm"
                                  style={{ background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}
                                  onClick={() => setReminderModal({ inv: { ...inv, client }, type: 'reminder' })}
                                >
                                  {t('facturen.herinneringKnop')}
                                </button>
                              )}
                              {showFinalBtn && (
                                <button
                                  className="btn btn-sm"
                                  style={{ background: '#ea580c', color: '#fff', borderColor: '#ea580c' }}
                                  onClick={() => setReminderModal({ inv: { ...inv, client }, type: 'final_notice' })}
                                >
                                  {t('facturen.aanmaningKnop')}
                                </button>
                              )}
                              {showIncassoBtn && (
                                <button
                                  className="btn btn-sm"
                                  style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                                  disabled={actionLoading === inv.id}
                                  onClick={() => setIncassoConfirm(inv)}
                                >
                                  {t('facturen.incassoKnop')}
                                </button>
                              )}
                              {/* Gedeeltelijk betaald (6) */}
                              {['sent', 'reminded', 'final_notice'].includes(inv.status) && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#92400e', borderColor: '#92400e' }}
                                  onClick={() => setGedeeltelijkModal(inv)}
                                >
                                  {t('facturen.gedeeltelijkBetaald')}
                                </button>
                              )}
                              {/* Volledig betaald bij partially_paid (6) */}
                              {inv.status === 'partially_paid' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setVolledigBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.volledigBetaaldKnop')}
                                </button>
                              )}
                              {(inv.status === 'sent' || inv.status === 'paid') && !inv.is_credit_note && !inv.has_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#ea580c', borderColor: '#ea580c' }}
                                  disabled={actionLoading === inv.id}
                                  onClick={() => setCreditnotaConfirm(inv)}
                                >
                                  {t('facturen.creditnota')}
                                </button>
                              )}
                              {inv.status === 'draft' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#dc2626', borderColor: '#dc2626' }}
                                  disabled={actionLoading === inv.id}
                                  onClick={() => setDeleteConfirm(inv)}
                                >
                                  {t('facturen.verwijderen')}
                                </button>
                              )}
                              {hasTimeline && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => toggleTimeline(inv.id)}
                                >
                                  {expandedTimelines.has(inv.id) ? '▲' : '▼'} {t('facturen.herinneringGeschieden')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedTimelines.has(inv.id) && hasTimeline && (
                          <tr style={{ background: 'var(--color-bg)' }}>
                            <td colSpan={6} style={{ paddingTop: 8, paddingBottom: 14, paddingLeft: 24 }}>
                              <div style={{ borderLeft: '3px solid var(--color-border)', paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[...inv.invoice_reminders]
                                  .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at))
                                  .map((r, i) => (
                                    <div key={i} style={{ fontSize: 12 }}>
                                      <span style={{ color: 'var(--color-text-muted)', marginRight: 8 }}>{fmtDate(r.sent_at)}</span>
                                      <span style={{ fontWeight: 600 }}>
                                        {r.type === 'reminder' ? t('facturen.histTypeReminder') :
                                         r.type === 'final_notice' ? t('facturen.histTypeFinalNotice') :
                                         t('facturen.histTypeCollections')}
                                      </span>
                                      {r.new_due_date && (
                                        <span style={{ marginLeft: 8, color: 'var(--color-text-muted)' }}>
                                          {t('facturen.histNieuweBetaaldatum')} {fmtDate(r.new_due_date)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Overeenkomsten (BUG 8: volledig herschreven) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            {t('clientDetail.overeenkomstenTitel')}
          </h2>
          {/* "Stuur overeenkomst" alleen zichtbaar als er GEEN openstaand token is en het profiel niet verwijderd is */}
          {!client.deleted_at && pendingTokens.length === 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/coach/overeenkomst/opstellen/${client.id}`)}>
              {t('agreement.stuurOvereenkomst')}
            </button>
          )}
        </div>

        {/* Openstaande tokens */}
        {pendingTokens.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>
              {t('agreement.openstaandTokensTitel')}
            </div>
            {pendingTokens.map(pt => (
              <div
                key={pt.id}
                style={{
                  padding: '10px 14px',
                  marginBottom: 8,
                  borderRadius: 8,
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                      ⏳ {t('agreement.statusOpenstaand')}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('agreement.verstuurdOp')} {fmtDate(pt.created_at)}
                      {' · '}
                      {t('agreement.verlooptOp')} {fmtDate(pt.expires_at)}
                      {pt.membership_type?.name && ` · ${pt.membership_type.name}`}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--color-danger)', fontSize: 12, flexShrink: 0 }}
                    onClick={() => setRevokeConfirm(pt.id)}
                  >
                    {t('agreement.intrekkenKnop')}
                  </button>
                </div>
                {/* BUG 16: Kopieer link + opnieuw sturen */}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12 }}
                    onClick={() => handleCopyLink(pt.token, pt.id)}
                  >
                    {copiedTokenId === pt.id ? t('agreement.linkGekopieerd') : t('agreement.kopieerLink')}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12 }}
                    onClick={() => handleResendToken(pt)}
                    disabled={resendingTokenId === pt.id}
                  >
                    {resendingTokenId === pt.id ? '…' : t('agreement.stuurOpnieuw')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ondertekende overeenkomsten */}
        {agreements.length === 0 ? (
          <p className="text-muted">
            {pendingTokens.length > 0
              ? t('agreement.geenOndertekendNogWachten')
              : t('clientDetail.geenOvereenkomsten')}
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clientDetail.colOndertekenddatum')}</th>
                  <th>{t('clientDetail.colAbonnementstype')}</th>
                  <th style={{ textAlign: 'right' }}>{t('clientDetail.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {agreements.map(agr => {
                  // Alle memberships gekoppeld aan deze overeenkomst (via signed_agreement_id)
                  const linkedMemberships = agr.client_memberships || [];
                  // Fallback: als er geen linked memberships zijn (oudere records), toon hoofdtype
                  const membershipDisplay = linkedMemberships.length > 0
                    ? linkedMemberships
                    : [{ membership_types: agr.membership_type, id: agr.id + '_fallback', initial_duration_months: null, payment_frequency: null, price_per_period: null, price_per_month: null, staffel_warning: null }];
                  return (
                    <tr key={agr.id}>
                      <td className="text-muted" style={{ verticalAlign: 'top', paddingTop: 12 }}>{fmtDate(agr.signed_at)}</td>
                      <td>
                        {membershipDisplay.map((m, idx) => {
                          const typeNm = m.membership_types?.name || t('clientDetail.onbekend');
                          const duur = m.initial_duration_months
                            ? `${m.initial_duration_months} mnd`
                            : null;
                          const prijsLabel = m.payment_frequency === 'month' && m.price_per_month
                            ? `${euro(m.price_per_month)}/mnd`
                            : m.price_per_period
                            ? euro(m.price_per_period)
                            : null;
                          return (
                            <div key={m.id} style={{ marginBottom: idx < membershipDisplay.length - 1 ? 6 : 0 }}>
                              <span className="font-medium">{typeNm}</span>
                              {duur && <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>— {duur}</span>}
                              {prijsLabel && <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>— {prijsLabel}</span>}
                              {m.staffel_warning && (
                                <div style={{ fontSize: 11, color: '#92400e', background: '#fef9c3', borderRadius: 4, padding: '2px 6px', marginTop: 2 }}>
                                  ⚠️ {m.staffel_warning}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </td>
                      <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: 10 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 13 }}
                          onClick={() => downloadAgreementPDF(agr.pdf_url)}
                        >
                          📄 {t('clientDetail.downloadPdf')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BUG 18: Tokenoverzicht (alle statussen) */}
      {allTokens.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t('agreement.allTokensTitel')}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('agreement.datumVerzonden')}</th>
                  <th>{t('clientDetail.colAbonnementstype')}</th>
                  <th>Status</th>
                  <th>{t('agreement.geldigTm')}</th>
                </tr>
              </thead>
              <tbody>
                {allTokens.map(tk => {
                  const now = new Date();
                  const isUsed = !!tk.used_at;
                  const isRejected = !!tk.rejected_at;
                  const isExpired = !isUsed && !isRejected && new Date(tk.expires_at) < now;
                  const isActive = !isUsed && !isRejected && !isExpired;
                  const wasRevokedByCoach = isRejected && tk.rejected_by !== tk.client_id;
                  const statusLabel = isUsed ? t('agreement.statusOndertekendLabel')
                    : isRejected && wasRevokedByCoach ? t('agreement.statusIngetrokkenLabel')
                    : isRejected ? t('agreement.statusAfgewezenLabel')
                    : isExpired ? t('agreement.statusVerlopenLabel')
                    : t('agreement.statusOpenstaandLabel');
                  const statusColor = isUsed ? '#16a34a' : isRejected ? '#dc2626' : isExpired ? '#9ca3af' : '#d97706';
                  return (
                    <tr key={tk.id}>
                      <td className="text-muted">{fmtDate(tk.created_at)}</td>
                      <td>{tk.membership_type?.name || '—'}</td>
                      <td>
                        <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>
                          {statusLabel}
                        </span>
                        {isRejected && tk.rejection_reason && (
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {t('agreement.redenAfwijzing')}{tk.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="text-muted">{fmtDate(tk.expires_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Intrekken bevestigingsdialoog */}
      {revokeConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('agreement.intrekkenTitel')}</h2>
              <button className="modal-close" onClick={() => setRevokeConfirm(null)} disabled={revoking}>✕</button>
            </div>
            <p style={{ marginBottom: 20, fontSize: 14, color: 'var(--color-text-muted)' }}>
              {t('agreement.intrekkenBevestig')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setRevokeConfirm(null)} disabled={revoking}>
                {t('agreement.annuleren')}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleRevokeToken(revokeConfirm)}
                disabled={revoking}
              >
                {revoking ? t('agreement.intrekkenBezig') : t('agreement.intrekkenBevestigKnop')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Communicatielogboek */}
      <div className="card" style={{ marginBottom: 24, marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{t('communication.title')}</h2>

        {/* BUG 6: Geplande toekomstige berichten */}
        {(() => {
          const now = new Date();
          const activePendingTokens = pendingTokens.filter(
            pt => !pt.used_at && !pt.rejected_at && new Date(pt.expires_at) > now
          );
          if (activePendingTokens.length === 0) return null;

          const intervalDays = agrReminderSettings.interval_days ?? 3;
          const maxReminders = agrReminderSettings.max_reminders ?? 2;
          const isEnabled = agrReminderSettings.enabled !== false;

          if (!isEnabled) return null;

          const geplande = [];
          activePendingTokens.forEach(pt => {
            const sentForToken = commLog.filter(
              e => e.type === 'agreement_reminder' && e.reference_id === pt.id
            );
            const sentCount = sentForToken.length;
            const remaining = maxReminders - sentCount;
            if (remaining <= 0) return;

            const lastDate = sentForToken.length > 0
              ? new Date(Math.max(...sentForToken.map(e => new Date(e.sent_at).getTime())))
              : new Date(pt.created_at);

            for (let i = 1; i <= remaining; i++) {
              const planned = new Date(lastDate.getTime() + i * intervalDays * 86400000);
              if (testmodus || planned > now) {
                geplande.push({
                  date: planned,
                  isNow: testmodus || planned <= now,
                  tokenId: pt.id,
                  tokenObj: pt,
                  tokenNaam: pt.membership_type?.name || null,
                  seqNum: sentCount + i,
                });
              }
            }
          });

          if (geplande.length === 0) return null;
          geplande.sort((a, b) => a.date - b.date);

          return (
            <div style={{
              marginBottom: 16,
              border: '1px dashed #0e7490',
              borderRadius: 6,
              padding: '10px 14px',
              background: '#f0f9ff',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0e7490', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('communication.geplandLabel')}
              </div>
              {geplande.map((g, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13, color: '#374151', marginBottom: 4 }}>
                  <span style={{ color: '#9ca3af', minWidth: 130 }}>
                    {g.date.toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <span style={{
                    background: '#e0f2fe', color: '#0369a1',
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  }}>
                    {t('communication.types.agreement_reminder')}
                  </span>
                  <span style={{ color: '#6b7280' }}>
                    {t('communication.geplandLabel')} #{g.seqNum}
                    {g.tokenNaam ? ` — ${g.tokenNaam}` : ''}
                  </span>
                  {g.isNow && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
                      onClick={() => handleResendToken(g.tokenObj)}
                      disabled={resendingTokenId === g.tokenId}
                    >
                      {resendingTokenId === g.tokenId ? '…' : t('communication.stuurNuKnop')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Prospect herinnering knop — zichtbaar als wachtdagen verstreken zijn of testmodus AAN */}
        {client?.client_status === 'prospect' && (() => {
          const prospectIntervalDays = parseInt(company?.prospect_reminder_interval_days || '7', 10);
          const lastPR = commLog
            .filter(e => e.type === 'prospect_reminder')
            .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0];
          const daysSinceLast = lastPR
            ? Math.floor((Date.now() - new Date(lastPR.sent_at).getTime()) / 86400000)
            : Infinity;
          if (!testmodus && daysSinceLast < prospectIntervalDays) return null;
          return (
            <div style={{ marginBottom: 16 }}>
              <button
                className="btn btn-sm"
                style={{ background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }}
                onClick={handleSendProspectReminder}
                disabled={sendingProspectReminder}
              >
                {sendingProspectReminder ? '…' : t('communication.stuurProspectHerinnering')}
              </button>
            </div>
          );
        })()}

        {commLoading && commLog.length === 0 ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : commLog.length === 0 ? (
          <p className="text-muted">{t('communication.empty')}</p>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('communication.colDatum')}</th>
                    <th>{t('communication.colType')}</th>
                    <th>{t('communication.colOnderwerp')}</th>
                    <th>{t('communication.colBericht')}</th>
                  </tr>
                </thead>
                <tbody>
                  {commLog.map((item, idx) => {
                    const rowKey = `${item.type}-${item.sent_at}-${idx}`;
                    const isExpanded = expandedRow === rowKey;
                    const isExpandable = ['push', 'message'].includes(item.type) && !!item.body?.trim();
                    const channelIcon = item.channel === 'in_app' ? '💬' : item.channel === 'push' ? '🔔' : '⚙';
                    const typeCfg = {
                      message:                   { label: t('communication.types.message'),                   bg: '#1d6fb5', color: '#fff' },
                      push:                      { label: t('communication.types.push'),                      bg: '#7c3aed', color: '#fff' },
                      reminder:                  { label: t('communication.types.reminder'),                  bg: '#d97706', color: '#fff' },
                      final_notice:              { label: t('communication.types.final_notice'),              bg: '#dc2626', color: '#fff' },
                      collections:               { label: t('communication.types.collections'),               bg: '#7f1d1d', color: '#fff' },
                      agreement_sent:            { label: t('communication.types.agreement_sent'),            bg: '#0369a1', color: '#fff' },
                      agreement_signed:          { label: t('communication.types.agreement_signed'),          bg: '#15803d', color: '#fff' },
                      agreement_rejected:        { label: t('communication.types.agreement_rejected'),        bg: '#be123c', color: '#fff' },
                      agreement_revoked:         { label: t('communication.types.agreement_revoked'),         bg: '#92400e', color: '#fff' },
                      agreement_reminder:        { label: t('communication.types.agreement_reminder'),        bg: '#0e7490', color: '#fff' },
                      prospect_reminder:         { label: t('communication.types.prospect_reminder'),         bg: '#7c3aed', color: '#fff' },
                      welcome:                   { label: t('communication.types.welcome'),                   bg: '#0d9488', color: '#fff' },
                      cancellation_confirmation: { label: t('communication.types.cancellation_confirmation'), bg: '#92400e', color: '#fff' },
                    };
                    const cfg = typeCfg[item.subtype] || typeCfg[item.type] || { label: item.subtype || item.type, bg: '#6b7280', color: '#fff' };
                    const isDetailType = ['reminder', 'final_notice', 'collections'].includes(item.type);
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          onClick={isExpandable ? () => setExpandedRow(prev => prev === rowKey ? null : rowKey) : undefined}
                          style={isExpandable ? { cursor: 'pointer' } : undefined}
                        >
                          <td className="text-muted" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                            {item.sent_at ? new Date(item.sent_at).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span style={{
                              background: cfg.bg, color: cfg.color,
                              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}>
                              {channelIcon} {cfg.label}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, fontSize: 13 }}>
                            {isDetailType && item.body?.trim() ? (
                              <>
                                <div style={{ fontWeight: 600 }}>{item.subject || '—'}</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{item.body}</div>
                              </>
                            ) : (
                              item.subject || '—'
                            )}
                          </td>
                          <td>
                            {isExpandable ? (
                              <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                                {isExpanded ? '▲' : '▼'}
                              </span>
                            ) : (
                              !isDetailType && (
                                <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>—</span>
                              )
                            )}
                          </td>
                        </tr>
                        {isExpandable && isExpanded && (
                          <tr>
                            <td colSpan={4} style={{ padding: '8px 16px', backgroundColor: '#f9fafb', color: '#374151', fontSize: '0.875rem' }}>
                              {item.body}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {commHasMore && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => loadCommLog(false)} disabled={commLoading}>
                  {commLoading ? t('communication.loading') : t('communication.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showNieuweFactuur && (
        <NieuweFactuurModal
          clientId={id}
          clientName={client.full_name || client.email}
          memberships={memberships}
          onClose={() => setShowNieuweFactuur(false)}
          onCreated={() => { setShowNieuweFactuur(false); reloadInvoices(); }}
        />
      )}

      {deleteConfirm && (
        <ConfirmModal
          title={t('facturen.verwijderenTitel')}
          body={t('facturen.verwijderenTekst')}
          confirmLabel={t('facturen.verwijderen')}
          danger
          onConfirm={() => handleDeleteInvoice(deleteConfirm)}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {creditnotaConfirm && (
        <ConfirmModal
          title={t('facturen.creditnotaTitel')}
          body={`${t('facturen.creditnotaTekstPrefix')} ${creditnotaConfirm.invoice_number}${t('facturen.creditnotaTekstSuffix')}`}
          confirmLabel={t('facturen.creditnota')}
          onConfirm={() => handleCreditnotaInvoice(creditnotaConfirm)}
          onClose={() => setCreditnotaConfirm(null)}
        />
      )}

      {creditModal && (
        <CreditModal
          type={creditModal}
          memberships={memberships.filter(m => ['pt', 'pt_plus', 'small_group'].includes(m.session_type))}
          onClose={() => setCreditModal(null)}
          onDone={handleCreditDone}
        />
      )}

      {incassoConfirm && (
        <ConfirmModal
          title={t('facturen.incassoTitel')}
          body={t('facturen.incassoTekst')}
          confirmLabel={t('facturen.incassoBevestigen')}
          danger
          onConfirm={() => handleIncasso(incassoConfirm)}
          onClose={() => setIncassoConfirm(null)}
        />
      )}

      {/* Bevestiging betaald (5A + 5C + incasso) */}
      {betaaldConfirm && (
        <ConfirmModal
          title={t('facturen.betaaldBevestigenTitel')}
          body={betaaldConfirm.status === 'collections'
            ? t('facturen.collectionsNaarBetaaldTekst')
            : `Weet je zeker dat je factuur ${betaaldConfirm.invoice_number} als betaald wilt markeren?`}
          confirmLabel={t('facturen.betaaldBevestigenKnop')}
          onConfirm={() => { handleStatusChange(betaaldConfirm); setBetaaldConfirm(null); }}
          onClose={() => setBetaaldConfirm(null)}
        />
      )}

      {/* Bevestiging onbetaald (5B) */}
      {onbetaaldConfirm && (
        <ConfirmModal
          title={t('facturen.onbetaaldTitel')}
          body={`Weet je zeker dat je factuur ${onbetaaldConfirm.invoice_number} terug wilt zetten naar Verzonden?`}
          confirmLabel={t('facturen.onbetaaldKnopBevestigen')}
          onConfirm={() => handleMarkeerOnbetaald(onbetaaldConfirm)}
          onClose={() => setOnbetaaldConfirm(null)}
        />
      )}

      {/* Volledig betaald bevestiging (6) */}
      {volledigBetaaldConfirm && (
        <ConfirmModal
          title={t('facturen.volledigBetaaldTitel')}
          body={t('facturen.volledigBetaaldTekst')}
          confirmLabel={t('facturen.volledigBetaaldBevestigen')}
          onConfirm={() => handleVolledigBetaald(volledigBetaaldConfirm)}
          onClose={() => setVolledigBetaaldConfirm(null)}
        />
      )}

      {/* Gedeeltelijk betaald modal (6) */}
      {gedeeltelijkModal && (
        <GedeeltelijkModal
          inv={gedeeltelijkModal}
          onClose={() => setGedeeltelijkModal(null)}
          onSaved={() => { setGedeeltelijkModal(null); reloadInvoices(); }}
        />
      )}

      {reminderModal && (
        <ReminderModal
          inv={reminderModal.inv}
          type={reminderModal.type}
          initialLines={invoiceLines[reminderModal.inv.id]}
          company={company}
          onClose={() => setReminderModal(null)}
          onRegistered={reloadInvoices}
        />
      )}

      {kcalModal && (
        <KcalModal
          clientId={id}
          coachId={user.id}
          initialValues={kcalAssignment}
          onClose={() => setKcalModal(false)}
          onSaved={() => { setKcalModal(false); loadKcal(); }}
        />
      )}


      {/* F4-8: Blokkering opheffen bevestigingsdialoog */}
      {unblockConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('clientDetail.blokkeringOpheffen')}</h2>
              <button className="modal-close" onClick={() => setUnblockConfirm(false)} disabled={unblocking}>✕</button>
            </div>
            <p style={{ marginBottom: 24, fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {t('clientDetail.blokkeringOpheffenBevestiging').replace('{naam}', client?.full_name || '')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setUnblockConfirm(false)} disabled={unblocking}>
                {t('agreement.annuleren')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUnblockClient}
                disabled={unblocking}
              >
                {unblocking ? '...' : t('clientDetail.blokkeringOpheffen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welkomstmail bevestigingsdialoog */}
      {welkomstmailConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('welkomstmail.bevestigingTitel')}</h2>
              <button className="modal-close" onClick={() => setWelkomstmailConfirm(false)} disabled={stuuringWelkomstmail}>✕</button>
            </div>
            <p style={{ marginBottom: 24, fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {t('welkomstmail.bevestigingTekst')
                .replace('{naam}', client?.full_name || '')
                .replace('{email}', client?.email || '')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setWelkomstmailConfirm(false)} disabled={stuuringWelkomstmail}>
                {t('welkomstmail.annuleren')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStuurWelkomstmail}
                disabled={stuuringWelkomstmail}
              >
                {stuuringWelkomstmail ? t('auth.loading') : t('welkomstmail.versturen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BUG 13: Client verwijderen bevestigingsdialoog */}
      {deleteClientConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('clients.verwijderConfirmTitel')}</h2>
              <button className="modal-close" onClick={() => setDeleteClientConfirm(false)} disabled={deletingClient}>✕</button>
            </div>
            <p style={{ marginBottom: 12, fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {t('clients.verwijderConfirmTekst')}
            </p>
            <p style={{ marginBottom: 24, fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
              ⚠ {t('clients.verwijderWaarschuwing')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteClientConfirm(false)} disabled={deletingClient}>
                {t('agreement.annuleren')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteClient}
                disabled={deletingClient}
                style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}
              >
                {deletingClient ? t('clients.verwijderBezig') : t('clients.verwijderClient')}
              </button>
            </div>
          </div>
        </div>
      )}

      {opzegModal && (
        <ConfirmModal
          title={t('clientDetail.opzeggenKnop')}
          body={t('clientDetail.opzeggenBevestiging', { naam: client?.full_name || '', datum: fmtDate(opzegModal.cancDate) })}
          onConfirm={handleOpzeggenConfirm}
          onClose={() => setOpzegModal(null)}
          danger
        />
      )}

      {/* ── Actie 1: Beëindigen modal ────────────────────────────── */}
      {beeindigenModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setBeeindigenModal(null)}>
          <div style={{ background: 'var(--color-card)', borderRadius: 12, padding: 28, minWidth: 360, maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>{t('clientDetail.beeindigenModalTitel')}</h2>
            {beeindigenError && <div className="error-msg" style={{ marginBottom: 12 }}>{beeindigenError}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="radio" name="beeindig-optie" checked={beeindigenOptie === 'einde_periode'} onChange={() => setBeeindigenOptie('einde_periode')} />
                <span>{t('clientDetail.beeindigenPerEindeMaand', { datum: (() => {
                  const anker = membership?.last_credit_booking_at ? new Date(membership.last_credit_booking_at) : new Date(membership?.start_date + 'T00:00:00');
                  return fmtDate(new Date(anker.getTime() + 30 * 86400000).toISOString().slice(0, 10));
                })() })}</span>
              </label>
              {membership?.initial_end_date && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="radio" name="beeindig-optie" checked={beeindigenOptie === 'einde_contract'} onChange={() => setBeeindigenOptie('einde_contract')} />
                  <span>{t('clientDetail.beeindigenPerEindeContract')} ({fmtDate(membership.initial_end_date)})</span>
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="radio" name="beeindig-optie" checked={beeindigenOptie === 'aangepast'} onChange={() => setBeeindigenOptie('aangepast')} />
                <span>{t('clientDetail.beeindigenPerDatum')}</span>
              </label>
            </div>
            {beeindigenOptie === 'aangepast' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">{t('clientDetail.beeindigenDatumLabel')}</label>
                <input type="date" className="form-input" value={beeindigenDatum} onChange={e => setBeeindigenDatum(e.target.value)} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setBeeindigenModal(null)} disabled={beeindigenSaving}>{t('clientDetail.beeindigenAnnuleren')}</button>
              <button className="btn btn-danger" onClick={handleBeeindigenConfirm} disabled={beeindigenSaving}>
                {beeindigenSaving ? '…' : t('clientDetail.beeindigenBevestigen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Actie 2: Nieuw lidmaatschap modal ───────────────────── */}
      {nieuwModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('clientDetail.nieuwModalTitel')}</h2>
              <button className="modal-close" onClick={() => setNieuwModal(false)}>✕</button>
            </div>
            {nieuwError && <div className="error-msg" style={{ marginBottom: 12 }}>{nieuwError}</div>}
            <div className="form-group">
              <label className="form-label">{t('clientDetail.nieuwLabelType')}</label>
              <select className="form-input" value={nieuwForm.type_id} onChange={e => handleNieuwTypeChange(e.target.value)}>
                <option value="">{t('clientDetail.nieuwSelecteerType')}</option>
                {membershipTypes.map(mt => <option key={mt.id} value={mt.id}>{mt.name}</option>)}
              </select>
            </div>
            {nieuwForm.type_id && (
              <div className="form-group">
                <label className="form-label">{t('clientDetail.nieuwLabelStaffel')}</label>
                {nieuwStaffels.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: 13 }}>{t('clientDetail.nieuwGeenStaffels')}</p>
                ) : (
                  <select className="form-input" value={nieuwForm.staffel_id} onChange={e => handleNieuwStaffelChange(e.target.value)}>
                    <option value="">{t('clientDetail.nieuwSelecteerStaffel')}</option>
                    {nieuwStaffels.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.duration_months} {s.duration_months === 1 ? 'maand' : 'maanden'} — {euro(s.price_per_period)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {/* Betaalfrequentie — alleen zichtbaar als staffel is geselecteerd */}
            {nieuwForm.staffel_id && (() => {
              const sel = nieuwStaffels.find(s => s.id === nieuwForm.staffel_id);
              if (!sel) return null;
              const discountPct = Number(sel.period_discount_pct || 0);
              const heeftKorting = discountPct > 0 && ['period', 'both'].includes(sel.payment_frequency);
              const kortingsBedrag = heeftKorting ? sel.price_per_period * (discountPct / 100) : 0;
              const geprijsdPeriod = sel.price_per_period - kortingsBedrag;
              if (sel.payment_frequency === 'both') {
                return (
                  <div className="form-group">
                    <label className="form-label">{t('abonnementen.labelPaymentFrequency')}</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input type="radio" name="nieuw_payfreq" value="month" checked={nieuwForm.payment_frequency === 'month'} onChange={() => setNieuwForm(p => ({ ...p, payment_frequency: 'month' }))} />
                        {t('abonnementen.payFreqMonth')} — {euro(sel.price_per_month || 0)}/mnd
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input type="radio" name="nieuw_payfreq" value="period" checked={nieuwForm.payment_frequency === 'period'} onChange={() => setNieuwForm(p => ({ ...p, payment_frequency: 'period' }))} />
                        {heeftKorting
                          ? <span>{t('abonnementen.payFreqPeriod')} — <span style={{ textDecoration: 'line-through', color: 'var(--color-text-muted)' }}>{euro(sel.price_per_period)}</span> <strong style={{ color: 'var(--color-primary)' }}>{euro(geprijsdPeriod)}</strong> ({discountPct}% {t('overeenkomstBetaalfreq.kortingLabel')})</span>
                          : `${t('abonnementen.payFreqPeriod')} — ${euro(sel.price_per_period)} totaal`
                        }
                      </label>
                    </div>
                  </div>
                );
              }
              return (
                <div style={{ background: 'var(--color-gray-50)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: 'var(--color-text-muted)' }}>
                  {sel.payment_frequency === 'month'
                    ? `${t('abonnementen.payFreqMonth')}: ${euro(sel.price_per_month || 0)}/mnd`
                    : heeftKorting
                      ? <span>{t('abonnementen.payFreqPeriod')}: <span style={{ textDecoration: 'line-through' }}>{euro(sel.price_per_period)}</span> → <strong style={{ color: 'var(--color-primary)' }}>{euro(geprijsdPeriod)}</strong> ({discountPct}% {t('overeenkomstBetaalfreq.kortingLabel')}, {t('overeenkomstBetaalfreq.kortingBesparing')} {euro(kortingsBedrag)})</span>
                      : `${t('abonnementen.payFreqPeriod')}: ${euro(sel.price_per_period)} totaal`
                  }
                </div>
              );
            })()}
            <div className="form-group">
              <label className="form-label">{t('clientDetail.nieuwLabelStartdatum')}</label>
              <input type="date" className="form-input" value={nieuwForm.start_date} onChange={e => setNieuwForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('clientDetail.nieuwLabelPrijs')}</label>
                <input type="number" step="0.01" min="0" className="form-input" value={nieuwForm.prijs} onChange={e => setNieuwForm(p => ({ ...p, prijs: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('clientDetail.nieuwLabelCredits')}</label>
                <input type="number" min="0" className="form-input" value={nieuwForm.credits} onChange={e => setNieuwForm(p => ({ ...p, credits: e.target.value }))} />
              </div>
            </div>
            {nieuwForm.prijs !== '' && nieuwForm.staffel_id && (() => {
              const s = nieuwStaffels.find(x => x.id === nieuwForm.staffel_id);
              const t2 = membershipTypes.find(mt => mt.id === nieuwForm.type_id);
              const isC = parseFloat(nieuwForm.prijs) !== s?.price_per_period || parseInt(nieuwForm.credits) !== t2?.legacy_credits_per_period;
              return isC ? (
                <p style={{ fontSize: 12, color: 'var(--color-warning)', marginBottom: 8 }}>{t('clientDetail.nieuwCustomBadge')}</p>
              ) : null;
            })()}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={nieuwForm.welkomstmail} onChange={e => setNieuwForm(p => ({ ...p, welkomstmail: e.target.checked }))} />
                <span style={{ fontSize: 14 }}>{t('clientDetail.nieuwLabelWelkomstmail')}</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setNieuwModal(false)} disabled={nieuwSaving}>{t('clientDetail.nieuwAnnuleren')}</button>
              <button className="btn btn-primary" onClick={handleNieuwOpslaan} disabled={nieuwSaving || !nieuwForm.type_id || !nieuwForm.staffel_id}>
                {nieuwSaving ? '…' : t('clientDetail.nieuwBevestigen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Actie 3: Lidmaatschapshistorie ──────────────────────── */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('clientDetail.lidmaatschapHistorieTitel')}</h2>
          <button className="btn btn-ghost btn-sm" onClick={loadAlleHistorie} disabled={historieLoading}>
            {historieLoading ? '…' : t('clientDetail.historieGeschiedenisLabel')}
          </button>
        </div>
        {alleHistorie.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('clientDetail.historieColType')}</th>
                  <th>{t('clientDetail.historieColPeriode')}</th>
                  <th>{t('clientDetail.historieColPrijs')}</th>
                  <th>{t('clientDetail.historieColCredits')}</th>
                  <th>{t('clientDetail.historieColStatus')}</th>
                  <th>{t('clientDetail.historieColStart')}</th>
                  <th>{t('clientDetail.historieColEind')}</th>
                </tr>
              </thead>
              <tbody>
                {alleHistorie.map(h => {
                  const statusKey = h.status === 'active' ? 'historieStatusActief' : h.cancellation_date ? 'historieStatusOpgezegd' : 'historieStatusVerlopen';
                  return (
                    <tr key={h.id}>
                      <td>{h.membership_types?.name || t('clientDetail.historieOnbekendType')}</td>
                      <td>{h.initial_duration_months ? `${h.initial_duration_months}m` : '—'}</td>
                      <td>{euro(h.price_per_period || 0)}</td>
                      <td>{h.credits_per_period ?? '—'}</td>
                      <td><span className={`badge ${h.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>{t(`clientDetail.${statusKey}`)}</span></td>
                      <td>{fmtDate(h.start_date)}</td>
                      <td>{fmtDate(h.end_date) || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted" style={{ fontSize: 13 }}>{historieLoading ? '…' : t('clientDetail.historieGeenData')}</p>
        )}
      </div>
    </div>
  );
}

function GedeeltelijkModal({ inv, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totaal = inv.total_amount || 0;
  const ingevoerd = parseFloat(amount) || 0;
  const openstaand = totaal - ingevoerd;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!amount || ingevoerd <= 0 || ingevoerd >= totaal) {
      setError(t('facturen.gedeeltelijkValidatie'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('mark_partially_paid', {
        p_invoice_id: inv.id,
        p_amount_paid: ingevoerd,
      });
      if (rpcErr) throw rpcErr;
      onSaved();
    } catch (err) {
      setError(t('facturen.gedeeltelijkFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('facturen.gedeeltelijkModalTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkTotaal')}</label>
            <div className="form-input" style={{ background: 'var(--color-bg)', cursor: 'default' }}>{euro(totaal)}</div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkBetaaldLabel')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkOpenstaand')}</label>
            <div
              className="form-input"
              style={{ background: 'var(--color-bg)', cursor: 'default', color: ingevoerd > 0 && openstaand < 0 ? '#dc2626' : undefined }}
            >
              {ingevoerd > 0 ? euro(openstaand) : '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('facturen.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('facturen.gedeeltelijkOpslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReminderModal({ inv, type, initialLines, company, onClose, onRegistered }) {
  const isFinal = type === 'final_notice';
  const [lines, setLines] = useState(initialLines || []);
  const [loading, setLoading] = useState(!(initialLines && initialLines.length > 0));
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(company?.push_reminder_enabled === 'true');
  const [pushTekst, setPushTekst] = useState(company?.push_reminder_text || '');
  const [pushSent, setPushSent] = useState(false);

  const formatDutchDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  async function sendPush(newDueStr) {
    try {
      const clientId = inv.client_id || inv.client?.id;
      if (!clientId) return;
      const tekst = (pushTekst || '').replace('[datum]', formatDutchDate(newDueStr));
      const { data: { user } } = await supabase.auth.getUser();
      const { success } = await sendPushToClient(supabase, user.id, clientId, 'billing', 'Aanmaning', tekst);
      if (!success) return;
      setPushSent(true);
    } catch {
      // stil falen
    }
  }

  useEffect(() => {
    if (initialLines && initialLines.length > 0) return;
    async function loadLines() {
      const { data } = await supabase
        .from('invoice_lines')
        .select('id, description, quantity, unit_price, btw_percentage, amount')
        .eq('invoice_id', inv.id)
        .order('id');
      setLines(data || []);
      setLoading(false);
    }
    loadLines();
  }, []);

  async function handleDownload() {
    setRegistering(true);
    setError('');
    const newDue = addDays(new Date(), isFinal ? 14 : 7);
    const newDueStr = format(newDue, 'yyyy-MM-dd');
    const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
      p_invoice_id: inv.id,
      p_type: type,
      p_new_due_date: newDueStr,
    });
    if (rpcErr) {
      setError(t('facturen.herinneringFoutLaden') + rpcErr.message);
      setRegistering(false);
      return;
    }
    setRegistered(true);
    onRegistered();
    if (isFinal && pushEnabled) {
      await sendPush(newDueStr);
    }
    setRegistering(false);
  }

  const title = isFinal ? t('facturen.aanmaningModalTitel') : t('facturen.herinneringModalTitel');
  const filename = `${isFinal ? 'aanmaning' : 'herinnering'}-${inv.invoice_number}.pdf`;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {loading && <p style={{ color: 'var(--color-text-muted)', marginBottom: 20 }}>{t('facturen.herinneringModalLoading')}</p>}
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Push notificatie optie (alleen bij aanmaning) */}
        {isFinal && !loading && (
          <div style={{ marginBottom: 16, padding: '10px 12px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                id="push-toggle-cd"
                checked={pushEnabled}
                onChange={e => setPushEnabled(e.target.checked)}
                style={{ width: 14, height: 14, cursor: 'pointer' }}
              />
              <label htmlFor="push-toggle-cd" style={{ fontSize: 13, cursor: 'pointer' }}>
                {t('facturen.pushVersturen')}
              </label>
            </div>
            {pushEnabled && (
              <textarea
                className="form-input"
                rows={2}
                value={pushTekst}
                onChange={e => setPushTekst(e.target.value)}
                style={{ resize: 'vertical', fontSize: 12 }}
              />
            )}
            {pushSent && (
              <div style={{ fontSize: 12, color: 'var(--color-primary)', marginTop: 4 }}>Push verstuurd.</div>
            )}
          </div>
        )}

        {!registered && !loading && (
          <div style={{ marginBottom: 20 }}>
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={registering}
            >
              {registering ? t('facturen.herinneringModalLoading') : t('facturen.registrerenEnDownloadBtn')}
            </button>
          </div>
        )}

        {registered && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: 16 }}>
              {t('facturen.herinneringModalSucces')}
            </p>
            <PDFDownloadLink
              document={<HerinneringDocument invoice={inv} lines={lines} company={company} type={type} />}
              fileName={filename}
              style={{ textDecoration: 'none' }}
            >
              {({ loading: pdfLoading }) => (
                <button className="btn btn-primary" disabled={pdfLoading}>
                  {pdfLoading ? t('facturen.genererenPdf') : t('facturen.herinneringModalDownload')}
                </button>
              )}
            </PDFDownloadLink>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>{t('facturen.herinneringModalSluiten')}</button>
        </div>
      </div>
    </div>
  );
}

function PdfButton({ inv, onLoadLines, lines, company }) {
  const [ready, setReady] = useState(false);
  const [localLines, setLocalLines] = useState(lines || []);

  async function handleClick() {
    if (!lines) {
      const loaded = await onLoadLines(inv.id);
      setLocalLines(loaded);
    }
    setReady(true);
  }

  if (!ready) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={handleClick}>
        {t('common.pdf')}
      </button>
    );
  }

  return (
    <PDFDownloadLink
      document={<FactuurDocument invoice={inv} lines={localLines} company={company} labels={getFactuurPdfLabels()} statusLabels={getFactuurStatusLabels()} fmtDate={pdfFmtDate} />}
      fileName={`${inv.invoice_number}.pdf`}
      style={{ textDecoration: 'none' }}
    >
      {({ loading }) => (
        <button className="btn btn-ghost btn-sm" disabled={loading}>
          {loading ? '…' : t('common.pdf')}
        </button>
      )}
    </PDFDownloadLink>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span className="text-muted">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
